import { useEffect, useMemo, useRef, useState } from "react";
import Page35_TabelHistoryMT from "./Page35_TabelHistoryMT";
import Page36_TabelHistoryBan from "./Page36_TabelHistoryBan";
import { PageKonten9_Show } from "./Page9_Show";
import { ModalBanShow } from "./Page28_BanShow";
import { ShowPic } from "../components/ShowPic";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";

const CACHE_KEY = "laporan_cache_map_v1";
const VIEW_META_KEY = "laporan_view_meta_v1";
const REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN_PAGE34 = "dashboard-daftar-hadir-bulanan-page34-v1";
const REQ_GROUP_DASHBOARD_REKAP_ABSENSI_PEGAWAI = "dashboard-rekap-absensi-pegawai-v1";

const normalize = (v) => String(v ?? "").toLowerCase();
const formatHeaderLabel = (v) => String(v ?? "").replace(/_/g, " ").toUpperCase();
const normalizeNopolToken = (v) => String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const normalizeSeriToken = (v) => String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const CELL_MAX_WIDTH = 200;
const POSISI_COLUMNS = [
  ...Array.from({ length: 22 }, (_x, i) => `P-${String(i + 1).padStart(2, "0")}`),
  "S-1",
  "S-2",
  "S-3",
];
const TARGET_NUMBER_COLUMNS = new Set([
  "odo laporan",
  "odo awal",
  "kmt laporan",
  "kmt saat laporan",
  "last odo",
  "kmt",
  "kmt awal",
  "kmt akhir",
  "kmt sekarang",
  "kmt awal td",
  "jarak tempuh",
  "jarak tempuh sejak pasang",
  "aus awal",
  "aus akhir",
  "aus periode",
  "aus ban sejak pasang",
  "td",
  "td1",
  "td2",
  "td min",
  "td sekarang",
  "selisih td",
  "selisih tdmin",
  "durasi hari",
]);

const normalizeColumnName = (v) => String(v ?? "")
  .toLowerCase()
  .replace(/_/g, " ")
  .replace(/\s+/g, " ")
  .trim();

const isThousandDotColumn = (col) => TARGET_NUMBER_COLUMNS.has(normalizeColumnName(col));
const isPosisiColumn = (col) => /^P-\d{2}$/.test(String(col || "").toUpperCase()) || /^S-\d$/.test(String(col || "").toUpperCase());
const isTdLaporanColumn = (col) => normalizeColumnName(col) === "td laporan";
const isTdColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "td" || n === "td1" || n === "td2" || n === "td min" || n === "td sekarang" || n === "td laporan";
};
const isTdDashText = (value) => /^[^-]+-[^-]+-[^-]+-[^-]+$/.test(String(value ?? "").trim());
const isKmtLaporanColumn = (col) => normalizeColumnName(col) === "kmt laporan";
const isJarakTempuhPadColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "kmt awal" || n === "kmt akhir" || n === "jarak tempuh";
};
const isWearRateNumericPadColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "aus awal" || n === "aus akhir" || n === "aus periode" || n === "wear rate";
};
const isWearRateLastPositionNumericColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "kmt di posisi" || n === "aus di posisi" || n === "wear rate di position" || n === "wear rate di posisi";
};
const isWearRateSejakAwalNumericColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "kmt sd perhitungan aus" || n === "aus sd perhitungan" || n === "wear rate";
};
const isWearRatePerMtPositionNumericColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "kmt saat perhitungan aus"
    || n === "total aus"
    || n === "wear rate posisi"
    || n === "avg wear rate mt";
};
const isWearRatePerMtPositionDateColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "tgl inisial" || n === "tgl perhitungan aus";
};
const isIdMtColumn = (col) => normalizeColumnName(col) === "id mt";
const isIdBanColumn = (col) => normalizeColumnName(col) === "id ban";
const isIdBanTerkaitColumn = (col) => normalizeColumnName(col) === "id ban terkait";
const isInputOdoDayColumn = (col) => /^\d{2}$/.test(String(col || "").trim());
const INPUT_ODO_STATE_STYLES = {
  missing: { label: "Data Kosong", background: "#9ca3af", color: "#ffffff" },
  normal: { label: "Normal", background: "#ffffff", color: "#0f172a" },
  koreksi: { label: "Koreksi", background: "#ff8a00", color: "#000000" },
  mt_off: { label: "MT Off", background: "#fff200", color: "#000000" },
  odo_rusak: { label: "Odo rusak", background: "#ff1f1f", color: "#ffffff" },
};
const WR_TD_CORRECTION_WARNING_TOOLTIP = "Data WR perlu diteliti lagi karena terdapat koreksi TD pada periode perhitungan.";
const hasWearRateTdCorrectionWarning = (row) => Number(row?.__WR_TD_KOREKSI) === 1;
const INPUT_ODO_LEGEND = [
  INPUT_ODO_STATE_STYLES.missing,
  INPUT_ODO_STATE_STYLES.normal,
  INPUT_ODO_STATE_STYLES.koreksi,
  INPUT_ODO_STATE_STYLES.mt_off,
  INPUT_ODO_STATE_STYLES.odo_rusak,
];
const UPDATE_ODO_TRANSACTION_LEGEND = [
  INPUT_ODO_STATE_STYLES.normal,
  INPUT_ODO_STATE_STYLES.koreksi,
  INPUT_ODO_STATE_STYLES.mt_off,
  INPUT_ODO_STATE_STYLES.odo_rusak,
];
const isStokGudangRightColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "kmt" || n === "umur ban" || n === "umur di gudang" || n === "lama idle";
};
const isBanVsFormasiCenterColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "formasi roda" || n === "ban terpasang" || n === "formasi cadangan" || n === "ban cadangan";
};
const isHiddenReportColumn = (col) => {
  const n = normalizeColumnName(col);
  if (String(col || "").startsWith("__")) return true;
  return n === "id ban" || n === "id mt" || n === "cabang" || n === "id supplier from place";
};
const isLampiranColumn = (col) => normalizeColumnName(col) === "lampiran";
const isFotoColumn = (col) => normalizeColumnName(col) === "foto";
const isPerubahanLikeColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "perubahan"
    || n === "perpindahan"
    || n === "perubahan posisi"
    || n === "perubahan status"
    || n === "perubahan lokasi";
};
const isEventMtRightNumberColumn = (col) => {
  const n = normalizeColumnName(col);
  return n === "odo" || n === "kmt tempuh";
};
const isEventBanKmtSaatEventColumn = (col) => normalizeColumnName(col) === "kmt saat event";
const isEventBanTdColumn = (col) => normalizeColumnName(col) === "td";

const parseVendorFromPlace = (placeValue) => {
  const raw = String(placeValue || "").trim();
  if (!raw) return "-";
  const [head, tail] = raw.split("#");
  if (String(head || "").trim().toLowerCase() !== "pabrik") return "-";
  const vendor = String(tail || "").trim();
  return vendor || "-";
};

const formatLokasiWearRate = (placeValue) => {
  const raw = String(placeValue || "").trim();
  if (!raw) return "-";
  if (/^K-[^#]*#.+$/i.test(raw)) {
    return raw.replace(/#/g, " | ");
  }
  const [head] = raw.split("#");
  return String(head || "").trim() || raw;
};

const formatThousandsDot = (value) => {
  if (value === null || value === undefined) return "-";
  const raw = String(value).trim();
  if (!raw) return "-";
  const digits = raw.replace(/[^\d-]/g, "");
  if (!digits || digits === "-") return raw;
  const num = Number(digits);
  if (!Number.isFinite(num)) return raw;
  return new Intl.NumberFormat("id-ID").format(num);
};

const formatDecimalId = (value, fractionDigits = 2) => {
  if (value === null || value === undefined) return "-";
  const raw = String(value).trim();
  if (!raw || raw.toLowerCase() === "null") return "-";
  const normalized = raw.replace(",", ".");
  const num = Number(normalized);
  if (!Number.isFinite(num)) return raw;
  return new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

const JAKARTA_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const JAKARTA_MONTH_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Jakarta",
  year: "numeric",
  month: "2-digit",
});

const formatYYYYMMDD = (date) => {
  if (!date) return null;
  const raw = String(date).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(raw)) {
    return raw.slice(0, 10);
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return JAKARTA_DATE_FORMATTER.format(d);
};

const toDateOnly = (value) => formatYYYYMMDD(value) || "";
const toDateOnlyStable = (value) => {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return toDateOnly(value);
};
const formatWearRatePerMtPositionDate = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  return toDateOnlyStable(raw) || raw;
};

const formatPosisiMasterBan = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";
  if (raw.startsWith("K-")) {
    return raw.replace(/#/g, " | ");
  }
  const [head] = raw.split("#");
  return String(head || "").trim() || raw;
};

const diffDays = (startValue, endValue) => {
  const s = toDateOnly(startValue);
  const e = toDateOnly(endValue);
  if (!s || !e) return "";
  const [sy, sm, sd] = s.split("-").map((x) => Number(x));
  const [ey, em, ed] = e.split("-").map((x) => Number(x));
  if (![sy, sm, sd, ey, em, ed].every(Number.isFinite)) return "";
  const startUtc = Date.UTC(sy, sm - 1, sd);
  const endUtc = Date.UTC(ey, em - 1, ed);
  const diff = Math.floor((endUtc - startUtc) / (24 * 60 * 60 * 1000));
  return diff >= 0 ? diff : "";
};

const toInt = (value, fallback = 0) => {
  const n = Number(String(value ?? "").replace(/[^\d-]/g, ""));
  return Number.isFinite(n) ? n : fallback;
};

const getPosisiVisualState = (row, col) => {
  const name = String(col || "").toUpperCase();
  if (!isPosisiColumn(name)) return "normal";
  const rodaCount = Math.max(0, Math.min(22, toInt(row?.roda, 0)));
  const cadanganCount = Math.max(0, Math.min(3, toInt(row?.cadangan, 0)));
  const rawVal = String(row?.[col] ?? "").trim();
  const isEmpty = !rawVal || rawVal === "-";

  if (/^P-\d{2}$/.test(name)) {
    const idx = Number(name.slice(2));
    if (idx > rodaCount) return "inactive";
    if (idx <= rodaCount && isEmpty) return "missing";
    return "normal";
  }

  if (/^S-\d$/.test(name)) {
    const idx = Number(name.slice(2));
    if (idx > cadanganCount) return "inactive";
    return "normal";
  }

  return "normal";
};

const getInputOdoVisualState = (row, col, isInputOdoMt) => {
  if (!isInputOdoMt || !isInputOdoDayColumn(col)) return "normal";
  const raw = String(row?.[col] ?? "").trim();
  if (!raw || raw === "-" || raw === "0" || raw === "0.0") return "missing";
  const n = Number(raw.replace(/[^\d.-]/g, ""));
  if (Number.isFinite(n) && n === 0) return "missing";
  const metaState = String(row?.[`__odo_state_${String(col || "").trim()}`] || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(INPUT_ODO_STATE_STYLES, metaState) ? metaState : "normal";
};

const getInputOdoCellStyle = (state) => (
  INPUT_ODO_STATE_STYLES[String(state || "").trim().toLowerCase()] || INPUT_ODO_STATE_STYLES.normal
);

const getUpdateOdoTransactionState = (value) => {
  const state = String(value ?? "")
    .split("|")[0]
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return ["normal", "koreksi", "mt_off", "odo_rusak"].includes(state) ? state : "";
};

const parseMaybeJson = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "object") return value;
  const raw = String(value || "").trim();
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return fallback;
  }
};

const toKeyValueText = (value) => {
  const toFlat = (obj) => Object.entries(obj || {})
    .map(([k, v]) => `${k}:${v === null || v === undefined ? "" : String(v)}`)
    .join(" | ");

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return toFlat(value);
  }
  if (typeof value === "string") {
    const raw = value.trim();
    if (raw.startsWith("{") && raw.endsWith("}")) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return toFlat(parsed);
        }
      } catch (_err) {}
    }
  }
  return String(value ?? "-");
};

const moveUserToLast = (cols) => {
  const arr = Array.isArray(cols) ? cols : [];
  const withoutUser = arr.filter((col) => String(col || "").toLowerCase() !== "user");
  return arr.some((col) => String(col || "").toLowerCase() === "user")
    ? [...withoutUser, "user"]
    : withoutUser;
};

const formatLampiranSummary = (value) => {
  const rows = (() => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  })();
  if (!rows.length) return "-";
  const captions = rows
    .map((item, idx) => String(item?.caption || item?.name || item?.file || `Lampiran ${idx + 1}`).trim())
    .filter(Boolean);
  if (!captions.length) return `Lampiran(${rows.length})`;
  return `Lampiran(${rows.length}): ${captions.join(", ")}`;
};

const dateKeyToJakarta = (value) => {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const direct = formatYYYYMMDD(raw);
  return direct || "";
};

const getJakartaTodayKey = () => JAKARTA_DATE_FORMATTER.format(new Date());
const getJakartaMonthKey = () => JAKARTA_MONTH_FORMATTER.format(new Date());
const formatMonthLabel = (value) => {
  const raw = String(value || "").trim();
  if (!/^\d{4}-\d{2}$/.test(raw)) return raw || "-";
  const [year, month] = raw.split("-").map((x) => Number(x));
  const date = new Date(Date.UTC(year, Math.max(0, month - 1), 1));
  return new Intl.DateTimeFormat("id-ID", {
    timeZone: "Asia/Jakarta",
    month: "long",
    year: "numeric",
  }).format(date);
};
const buildRecentMonthOptions = (count = 12) => {
  const total = Math.max(1, Number(count) || 1);
  const now = new Date();
  const baseMonth = Number(JAKARTA_MONTH_FORMATTER.format(now).slice(5, 7)) - 1;
  const baseYear = Number(JAKARTA_MONTH_FORMATTER.format(now).slice(0, 4));
  return Array.from({ length: total }, (_item, index) => {
    const date = new Date(Date.UTC(baseYear, baseMonth - index, 1));
    const value = JAKARTA_MONTH_FORMATTER.format(date);
    return {
      value,
      label: formatMonthLabel(value),
    };
  });
};

const buildAbsensiStatusStyle = (statusRaw, isFutureDate) => {
  if (isFutureDate) {
    return { background: "#ffffff", color: "#0f172a" };
  }
  const status = String(statusRaw || "").trim().toUpperCase();
  if (!status || status === "-" || status === "BELUM ABSEN") {
    return { background: "#E5E7EB", color: "#475569" };
  }
  if (status === "HADIR") {
    return { background: "#000670f2", color: "#ffffff" };
  }
  if (status === "LIBUR") {
    return { background: "#4a8a00", color: "#ffffff" };
  }
  if (status === "IJIN" || status === "IZIN") {
    return { background: "#007a82", color: "#ffffff" };
  }
  if (status === "SAKIT") {
    return { background: "#6e006a", color: "#ffffff" };
  }
  if (status === "CUTI") {
    return { background: "#1b5e00", color: "#ffffff" };
  }
  if (status === "ALPA" || status === "TANPA KETERANGAN") {
    return { background: "#dd0000", color: "#ffffff" };
  }
  if (status === "DINAS LUAR" || status === "DL") {
    return { background: "#fbee00", color: "#000000" };
  }
  if (status === "NON AKTIF" || status === "NONAKTIF") {
    return { background: "#fb9200", color: "#000000" };
  }
  return { background: "#F8FAFC", color: "#334155" };
};

const normalizeExcelColor = (value, fallback = "#FFFFFF") => {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toUpperCase();
  if (/^#[0-9a-fA-F]{8}$/.test(raw)) return `#${raw.slice(1, 7).toUpperCase()}`;
  return fallback;
};

function parseJson(key, fallback) {
  if (typeof window === "undefined") return fallback;
  const state = window.__laporan_runtime_state;
  if (!state || typeof state !== "object") return fallback;
  if (key === CACHE_KEY) return state.cacheMap && typeof state.cacheMap === "object" ? state.cacheMap : fallback;
  if (key === VIEW_META_KEY) return state.viewMeta && typeof state.viewMeta === "object" ? state.viewMeta : fallback;
  return fallback;
}

export function PageKonten34_ShowDataLaporan({ setPage: navigatePage, opsiCabang = [], opsiBan = [], globalOpsi = {} }) {
  const topScrollRef = useRef(null);
  const bottomScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [_runtimeTick, setRuntimeTick] = useState(0);
  const [isDownloadingOdoAttachments, setIsDownloadingOdoAttachments] = useState(false);
  const viewMeta = parseJson(VIEW_META_KEY, { title: "LAPORAN MT", tanggal: "-" });
  const cacheMap = parseJson(CACHE_KEY, {});
  const activeReportKey = String(viewMeta?.report_key || "");
  const activePayload = (cacheMap && typeof cacheMap === "object") ? cacheMap?.[activeReportKey] : null;
  const rawRows = Array.isArray(activePayload?.rows) ? activePayload.rows : [];
  const isLaporanMt = String(activeReportKey || "").startsWith("LAPORAN_MT|");
  const isMasterBan = String(activeReportKey || "").startsWith("MASTER_BAN|");
  const isInputOdoMt = String(activeReportKey || "").startsWith("INPUT_ODO_MT|");
  const isLaporanHarian = String(activeReportKey || "").startsWith("LAPORAN_HARIAN|");
  const isLaporanUpdateOdo = isLaporanHarian && String(activeReportKey || "").includes("|update_odo|");
  const isLaporanEventMt = String(activeReportKey || "").startsWith("LAPORAN_EVENT_MT|");
  const isLaporanEventBan = String(activeReportKey || "").startsWith("LAPORAN_EVENT_BAN|");
  const isDashboardBelanjaYtd = String(activeReportKey || "").startsWith("DASHBOARD_BELANJA_YTD|");
  const isLaporanEventBanLike = isLaporanEventBan || isDashboardBelanjaYtd;
  const isLaporanKoreksiOdoTd = String(activeReportKey || "").startsWith("LAPORAN_KOREKSI_ODO_TD|");
  const isCekValidasiOdoKmt = String(activeReportKey || "").startsWith("CEK_VALIDASI_ODO_KMT|");
  const isJarakTempuhMt = String(activeReportKey || "").startsWith("JARAK_TEMPUH_MT|");
  const isWearRate = String(activeReportKey || "").startsWith("WEAR_RATE|");
  const isWearRateSejakAwal = String(activeReportKey || "").includes("|sejak_awal_pengadaan|");
  const isWearRateLastPosition = String(activeReportKey || "").includes("|otf_last_position|");
  const isWearRatePerMtPosition = String(activeReportKey || "").includes("|per_mt_posisi|");
  const isHistoryLinkTargetReport = (isLaporanHarian && String(activeReportKey || "").includes("|laporan_lengkap|"))
    || isWearRateLastPosition
    || isWearRatePerMtPosition;
  const isStokGudang = String(activeReportKey || "").startsWith("STOK_GUDANG|");
  const isStokPabrik = String(activeReportKey || "").startsWith("STOK_PABRIK|");
  const isBanVsFormasi = String(activeReportKey || "").startsWith("BAN_VS_FORMASI|");
  const isProfilVsEvent = String(activeReportKey || "").startsWith("PROFIL_VS_EVENT|");
  const isBanProfilVsLastEvent = String(activeReportKey || "").startsWith("BAN_PROFIL_VS_LAST_EVENT|");
  const isFormasiBanJanggal = String(activeReportKey || "").startsWith("FORMASI_BAN_JANGGAL|");
  const isMonitoringBan = String(activeReportKey || "").startsWith("MONITORING_BAN|");
  const isDashboardDaftarHadirBulanan = String(activeReportKey || "").startsWith("DASHBOARD_DAFTAR_HADIR_BULANAN|");
  const inputOdoPeriode = isInputOdoMt ? String(activeReportKey || "").split("|")[2] || "" : "";

  const cabangMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(opsiCabang) ? opsiCabang : []).forEach((x) => {
      const kode = String(x?.kode_cabang || "").trim();
      const nama = String(x?.nama_cabang || "").trim();
      if (kode) map.set(kode, nama || kode);
    });
    return map;
  }, [opsiCabang]);

  const banMap = useMemo(() => {
    const map = new Map();
    (Array.isArray(opsiBan) ? opsiBan : []).forEach((x) => {
      const id = String(x?.id_ban ?? "").trim();
      const seri = String(x?.seri_ban ?? "").trim();
      if (id) map.set(id, seri || id);
    });
    return map;
  }, [opsiBan]);
  const daftarHadirMonthOptions = useMemo(() => buildRecentMonthOptions(12), []);
  const daftarHadirReportMeta = useMemo(() => {
    const parts = String(activeReportKey || "").split("|");
    const activeMonth = /^\d{4}-\d{2}$/.test(parts[1] || "") ? String(parts[1]).trim() : "";
    const kodeCabangRaw = String(parts[2] || "").trim();
    const titleRaw = String(viewMeta?.title || "").trim();
    const titlePrefix = "ABSENSI BULANAN - ";
    const titleCabang = titleRaw.startsWith(titlePrefix) ? titleRaw.slice(titlePrefix.length).trim() : "";
    const isSemuaCabang = !kodeCabangRaw || kodeCabangRaw === "ALL" || kodeCabangRaw === "C-1";
    const namaCabang = titleCabang || (isSemuaCabang ? "Semua Cabang" : (cabangMap.get(kodeCabangRaw) || kodeCabangRaw || "Semua Cabang"));
    return {
      activeMonth,
      kodeCabangRaw,
      kodeCabangParam: isSemuaCabang ? "" : kodeCabangRaw,
      namaCabang,
    };
  }, [activeReportKey, viewMeta?.title, cabangMap]);

  const baseRows = useMemo(() => {
    if (!isLaporanMt && !isMasterBan && !isInputOdoMt && !isLaporanHarian && !isLaporanEventMt && !isLaporanEventBanLike && !isCekValidasiOdoKmt && !isJarakTempuhMt && !isWearRate && !isStokGudang && !isStokPabrik && !isBanVsFormasi && !isProfilVsEvent && !isBanProfilVsLastEvent && !isFormasiBanJanggal && !isMonitoringBan) return rawRows;
    return rawRows.map((row) => {
      const src = row && typeof row === "object" ? row : {};
      const next = { ...src };

      if (!isLaporanHarian && Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
        const kode = String(next.kode_cabang || "").trim();
        next.cabang = cabangMap.get(kode) || kode || "-";
        delete next.kode_cabang;
      }

      if (Object.prototype.hasOwnProperty.call(next, "tanggal_laporan")) {
        next.tanggal_laporan = toDateOnly(next.tanggal_laporan);
      }
      if (Object.prototype.hasOwnProperty.call(next, "tanggal")) {
        next.tanggal = toDateOnlyStable(next.tanggal);
      }

      if (isLaporanHarian) {
        Object.keys(next).forEach((key) => {
          const normalizedKey = String(key || "").trim().toLowerCase();
          const rawValue = next[key];
          if (!rawValue) return;
          if (!(normalizedKey.includes("tanggal") || normalizedKey.startsWith("tgl"))) return;
          next[key] = toDateOnlyStable(rawValue);
        });
      }

      if (isLaporanMt) {
        const daftarBanObj = parseMaybeJson(next?.daftar_ban, {});
        const reffObj = parseMaybeJson(next?.reff, {});
        const reffBanRows = Array.isArray(reffObj?.ban) ? reffObj.ban : [];
        const metaByPosisi = new Map();
        reffBanRows.forEach((item) => {
          const posisi = String(item?.posisi || "").trim().toUpperCase();
          if (!posisi) return;
          const indikasi = String(item?.indikasi || "").trim();
          const spakboard = String(item?.spakboard || "").trim();
          metaByPosisi.set(posisi, {
            indikasi: indikasi ? `INDIKASI: ${indikasi}` : "",
            spakboard: spakboard ? `SPAKBOARD: ${spakboard}` : "",
          });
        });

        POSISI_COLUMNS.forEach((posisi) => {
          const idBanRaw = daftarBanObj && typeof daftarBanObj === "object" ? daftarBanObj[posisi] : "";
          const idBan = String(idBanRaw ?? "").trim();
          const seriBan = idBan ? (banMap.get(idBan) || idBan) : "";
          const meta = metaByPosisi.get(posisi);
          const extras = [meta?.indikasi, meta?.spakboard].filter(Boolean).join(" | ");
          const text = [seriBan, extras].filter(Boolean).join(" | ");
          next[posisi] = text || "-";
        });

        delete next.daftar_ban;
        delete next.reff;
      }

      if (isMasterBan) {
        if (Object.prototype.hasOwnProperty.call(next, "status_terakhir")) {
          next.status = String(next.status_terakhir ?? "").trim();
          delete next.status_terakhir;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "nopol")) {
          if (Object.prototype.hasOwnProperty.call(next, "nopol_terakhir")) {
            next.nopol = String(next.nopol_terakhir ?? "").trim();
            delete next.nopol_terakhir;
          } else if (Object.prototype.hasOwnProperty.call(next, "nopol_laporan")) {
            next.nopol = String(next.nopol_laporan ?? "").trim();
            delete next.nopol_laporan;
          }
        }
        if (Object.prototype.hasOwnProperty.call(next, "posisi_terakhir")) {
          next.posisi = formatPosisiMasterBan(next.posisi_terakhir);
          delete next.posisi_terakhir;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "telapak") && Object.prototype.hasOwnProperty.call(next, "telapak_terakhir")) {
          next.telapak = String(next.telapak_terakhir ?? "").trim();
          delete next.telapak_terakhir;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "catatan_ban")) {
          if (Object.prototype.hasOwnProperty.call(next, "catatan_initial")) {
            next.catatan_ban = String(next.catatan_initial ?? "").trim();
            delete next.catatan_initial;
          } else if (Object.prototype.hasOwnProperty.call(next, "catatan")) {
            next.catatan_ban = String(next.catatan ?? "").trim();
          }
        }
        if (Object.prototype.hasOwnProperty.call(next, "tgl_pasang_awal")) {
          next.tgl_pasang_awal = toDateOnly(next.tgl_pasang_awal);
        }
      }

      if (isInputOdoMt) {
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
      }

      if (isJarakTempuhMt) {
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
        if (Object.prototype.hasOwnProperty.call(next, "tgl_awal")) {
          next.tgl_awal = toDateOnlyStable(next.tgl_awal);
        }
        if (Object.prototype.hasOwnProperty.call(next, "tgl_akhir")) {
          next.tgl_akhir = toDateOnlyStable(next.tgl_akhir);
        }
        if (Object.prototype.hasOwnProperty.call(next, "status_terakhir")) {
          next.status = String(next.status_terakhir || "").trim() || "-";
          delete next.status_terakhir;
        }
      }

      if (isWearRate) {
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
        if (Object.prototype.hasOwnProperty.call(next, "tgl_awal")) {
          next.tgl_awal = toDateOnlyStable(next.tgl_awal);
        }
        if (Object.prototype.hasOwnProperty.call(next, "tgl_akhir")) {
          next.tgl_akhir = toDateOnlyStable(next.tgl_akhir);
        }
        if (Object.prototype.hasOwnProperty.call(next, "status_terakhir")) {
          next.status = String(next.status_terakhir || "").trim() || "-";
          delete next.status_terakhir;
        }
        if (Object.prototype.hasOwnProperty.call(next, "place")) {
          next.lokasi = formatLokasiWearRate(next.place);
          delete next.place;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "aus_awal") && Object.prototype.hasOwnProperty.call(next, "tdmin_awal")) {
          next.aus_awal = next.tdmin_awal;
          delete next.tdmin_awal;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "aus_akhir") && Object.prototype.hasOwnProperty.call(next, "tdmin_akhir")) {
          next.aus_akhir = next.tdmin_akhir;
          delete next.tdmin_akhir;
        }
        if (!Object.prototype.hasOwnProperty.call(next, "aus_periode") && Object.prototype.hasOwnProperty.call(next, "delta_tdmin")) {
          next.aus_periode = next.delta_tdmin;
          delete next.delta_tdmin;
        }
        if (isWearRateSejakAwal) {
          ["TD_LAST_MOVE", "WEAR_LAST_MOVE", "TGL_PERHITUNGAN_AUS"].forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(next, key)) next[key] = toDateOnlyStable(next[key]);
          });
        } else if (isWearRateLastPosition) {
          ["TGL_PASANG_DI_POSISI", "TGL_PERHITUNGAN_AUS"].forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(next, key)) next[key] = toDateOnlyStable(next[key]);
          });
        } else if (isWearRatePerMtPosition) {
          ["TGL INISIAL", "TGL PERHITUNGAN AUS"].forEach((key) => {
            if (Object.prototype.hasOwnProperty.call(next, key)) next[key] = toDateOnlyStable(next[key]);
          });
        } else {
          next.durasi = (() => {
            const d = diffDays(next?.tgl_awal, next?.tgl_akhir);
            return d === "" ? "-" : `${d} hari`;
          })();
        }
      }

      if (isStokGudang) {
        if (Object.prototype.hasOwnProperty.call(next, "tanggal_laporan")) {
          next.tanggal_laporan = toDateOnly(next.tanggal_laporan);
        }
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
        next.status = String(next?.status ?? next?.status_laporan ?? next?.status_terakhir ?? "").trim() || "-";
        next.lokasi = "Gudang";
        next.kmt = String(next?.kmt ?? next?.kmt_laporan ?? "").trim() || "0";
        next.td = String(next?.td ?? next?.td_laporan ?? "").trim() || "-";
        next.tgl_inisial = toDateOnly(next?.tgl_inisial || "");
        next.umur_ban = (() => {
          const d = diffDays(next?.tgl_inisial, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
        next.tgl_masuk_gudang = toDateOnly(next?.tgl_masuk_gudang || "");
        next.umur_di_gudang = (() => {
          const d = diffDays(next?.tgl_masuk_gudang, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
        next.tgl_lepas = toDateOnly(next?.tgl_lepas || "");
        next.lama_idle = (() => {
          const d = diffDays(next?.tgl_lepas, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
        delete next.status_laporan;
        delete next.status_terakhir;
        delete next.kmt_laporan;
        delete next.td_laporan;
        delete next.posisi_laporan;
        delete next.posisi_terakhir;
        delete next.tgl_mulai_posisi;
        delete next.tgl_event_terakhir;
        delete next.status_inisial;
        delete next.event_inisial;
        delete next.event_terakhir;
        delete next.tgl_lepas_pasang;
        delete next.event_lepas_pasang;
        delete next.place_sebelumnya;
        delete next.place_sesudahnya;
        delete next.tgl_pasang_awal;
        delete next.mt_sebelumnya;
      }

      if (isStokPabrik) {
        if (Object.prototype.hasOwnProperty.call(next, "tanggal_laporan")) {
          next.tanggal_laporan = toDateOnly(next.tanggal_laporan);
        }
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
        next.status = String(next?.status ?? "").trim() || "-";
        next.vendor = parseVendorFromPlace(next?.place);
        next.kmt = String(next?.kmt ?? "").trim() || "0";
        next.td = String(next?.td ?? "").trim() || "-";
        next.tgl_inisial = toDateOnly(next?.tgl_inisial || "");
        next.umur_ban = (() => {
          const d = diffDays(next?.tgl_inisial, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
        next.tgl_masuk_pabrik = toDateOnly(next?.tgl_masuk_pabrik || "");
        next.umur_di_pabrik = (() => {
          const d = diffDays(next?.tgl_masuk_pabrik, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
        next.tgl_lepas = toDateOnly(next?.tgl_lepas || "");
        next.lama_idle = (() => {
          const d = diffDays(next?.tgl_lepas, next?.tanggal_laporan);
          return d === "" ? "-" : String(d);
        })();
      }

      if (isBanVsFormasi) {
        const kodeCabang = String(next?.kode_cabang ?? next?.cabang ?? "").trim();
        const idMt = String(next?.id_mt ?? next?.idmt ?? "").trim();
        const nopol = String(next?.nopol ?? next?.no_pol ?? next?.nomor_polisi ?? "").trim();
        const lastUpdateRaw = next?.last_update ?? next?.updated_at ?? next?.tanggal_update ?? next?.tgl_update ?? next?.lastupdate ?? "";
        const status = String(next?.status ?? next?.status_mt ?? "").trim();
        const formasiRoda = next?.formasi_roda ?? next?.roda ?? next?.formasi ?? "";
        const banTerpasang = next?.ban_terpasang ?? next?.terpasang ?? next?.ban_pasang ?? "";
        const formasiCadangan = next?.formasi_cadangan ?? next?.cadangan ?? "";
        const banCadangan = next?.ban_cadangan ?? next?.cadangan_terpasang ?? "";
        const statusUji = String(next?.status_uji ?? next?.uji_status ?? next?.status_check ?? "").trim();

        return {
          cabang: cabangMap.get(kodeCabang) || kodeCabang || "-",
          id_mt: idMt || "-",
          nopol: nopol || "-",
          last_update: toDateOnly(lastUpdateRaw) || "-",
          status: status || "-",
          formasi_roda: String(formasiRoda ?? "").trim() || "0",
          ban_terpasang: String(banTerpasang ?? "").trim() || "0",
          formasi_cadangan: String(formasiCadangan ?? "").trim() || "0",
          ban_cadangan: String(banCadangan ?? "").trim() || "0",
          status_uji: statusUji || "-",
        };
      }

      if (isProfilVsEvent) {
        const idMt = String(next?.id_mt ?? next?.idmt ?? "").trim();
        const parameterUji = String(next?.parameter_uji ?? next?.parameter ?? next?.uji_parameter ?? "").trim();
        const hasilUji = String(next?.hasil_uji ?? next?.hasil ?? next?.uji_hasil ?? "").trim();
        const statusUji = String(next?.status_uji ?? next?.status ?? next?.uji_status ?? "").trim();
        return {
          id_mt: idMt || "-",
          parameter_uji: parameterUji || "-",
          hasil_uji: hasilUji || "-",
          status_uji: statusUji || "-",
        };
      }

      if (isBanProfilVsLastEvent) {
        const idBan = String(next?.id_ban ?? "").trim();
        const seriBan = String(next?.seri_ban ?? "").trim();
        const parameterUji = String(next?.parameter_uji ?? next?.parameter ?? next?.uji_parameter ?? "").trim();
        const hasilUji = String(
          next?.ban_profil_vs_last_event
          ?? next?.hasil_uji
          ?? next?.hasil
          ?? next?.uji_hasil
          ?? ""
        ).trim();
        const statusUji = String(next?.status_uji ?? next?.status ?? next?.uji_status ?? "").trim();
        return {
          id_ban: idBan || "-",
          seri_ban: seriBan || "-",
          parameter_uji: parameterUji || "-",
          hasil_uji: hasilUji || "-",
          status_uji: statusUji || "-",
        };
      }

      if (isFormasiBanJanggal) {
        const daftarBanObj = parseMaybeJson(next?.daftar_ban, {});
        const daftarBanText = daftarBanObj && typeof daftarBanObj === "object"
          ? Object.entries(daftarBanObj).map(([k, v]) => `${k}:${String(v || "")}`).join(" | ")
          : String(next?.daftar_ban || "");
        return {
          id_mt: String(next?.id_mt || "").trim() || "-",
          nopol: String(next?.nopol || "").trim() || "-",
          status: String(next?.status || "").trim() || "-",
          roda: String(next?.roda ?? "").trim() || "0",
          cadangan: String(next?.cadangan ?? "").trim() || "0",
          kapasitas: String(next?.kapasitas ?? "").trim() || "-",
          daftar_ban: daftarBanText || "-",
        };
      }

      if (isMonitoringBan) {
        if (Object.prototype.hasOwnProperty.call(next, "kode_cabang")) {
          const kode = String(next.kode_cabang || "").trim();
          next.cabang = cabangMap.get(kode) || kode || "-";
          delete next.kode_cabang;
        }
      }

      if (isDashboardDaftarHadirBulanan) {
        if (Object.prototype.hasOwnProperty.call(next, "Tanggal")) {
          next.Tanggal = toDateOnlyStable(next.Tanggal);
        }
        if (Object.prototype.hasOwnProperty.call(next, "tanggal")) {
          next.tanggal = toDateOnlyStable(next.tanggal);
        }
      }

      return next;
    });
  }, [rawRows, isLaporanMt, isMasterBan, isInputOdoMt, isLaporanHarian, isLaporanEventMt, isLaporanEventBanLike, isCekValidasiOdoKmt, isJarakTempuhMt, isWearRate, isWearRateSejakAwal, isWearRateLastPosition, isWearRatePerMtPosition, isStokGudang, isStokPabrik, isBanVsFormasi, isProfilVsEvent, isBanProfilVsLastEvent, isFormasiBanJanggal, isMonitoringBan, isDashboardDaftarHadirBulanan, cabangMap, banMap]);

  const allColumns = useMemo(() => {
    const set = new Set();
    for (const row of baseRows) {
      Object.keys(row || {}).forEach((k) => set.add(k));
    }
    const cols = Array.from(set);
    if (isLaporanMt) {
      const priority = [
        "cabang",
        "tanggal_laporan",
        "id_mt",
        "nopol",
        "kapasitas",
        "kategori",
        "roda",
        "cadangan",
        "tahun_pembuatan",
        "odo_awal",
        "odo_laporan",
        "kmt_laporan",
        ...POSISI_COLUMNS,
      ];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x) && x !== "daftar_ban" && x !== "reff");
      return moveUserToLast([...head, ...tail]);
    }

    if (isMasterBan) {
      const priority = [
        "cabang",
        "tanggal_laporan",
        "seri_ban",
        "status",
        "nopol",
        "posisi",
        "kmt_laporan",
        "td_laporan",
        "ukuran",
        "merk",
        "id_suplier",
        "telapak",
        "catatan_ban",
      ];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isInputOdoMt) {
      const now = new Date();
      const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
      const currentDay = now.getDate();
      const dayCols = cols
        .filter((x) => isInputOdoDayColumn(x))
        .filter((x) => {
          if (inputOdoPeriode !== currentPeriod) return true;
          return Number(x) <= currentDay;
        })
        .sort((a, b) => Number(a) - Number(b));
      const priority = ["cabang", "periode", "id_mt", "nopol", ...dayCols];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x) && !isInputOdoDayColumn(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isLaporanUpdateOdo) {
      const priority = ["cabang", "tgl", "nopol", "odo", "transaksi", "keterangan", "lampiran", "user"];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isJarakTempuhMt) {
      const priority = [
        "cabang",
        "id_mt",
        "nopol",
        "tgl_awal",
        "tgl_akhir",
        "kmt_awal",
        "kmt_akhir",
        "jarak_tempuh",
        "status",
      ];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isWearRate) {
      return cols;
    }

    if (isStokGudang) {
      const priority = [
        "cabang",
        "tanggal_laporan",
        "id_ban",
        "seri_ban",
        "status",
        "vendor",
        "kmt",
        "td",
        "ukuran",
        "merk",
        "telapak",
        "tgl_inisial",
        "umur_ban",
        "tgl_masuk_gudang",
        "umur_di_gudang",
        "tgl_lepas",
        "lama_idle",
      ];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x) && x !== "mt_sebelumnya");
      return moveUserToLast([...head, ...tail]);
    }

    if (isStokPabrik) {
      const priority = [
        "tanggal_laporan",
        "cabang",
        "id_ban",
        "seri_ban",
        "status",
        "vendor",
        "kmt",
        "td",
        "ukuran",
        "merk",
        "telapak",
        "tgl_inisial",
        "umur_ban",
        "tgl_masuk_pabrik",
        "umur_di_pabrik",
        "tgl_lepas",
        "lama_idle",
      ];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x) && x !== "place" && x !== "lokasi");
      return moveUserToLast([...head, ...tail]);
    }

    if (isBanVsFormasi) {
      const canonical = [
        "cabang",
        "id_mt",
        "nopol",
        "last_update",
        "status",
        "formasi_roda",
        "ban_terpasang",
        "formasi_cadangan",
        "ban_cadangan",
        "status_uji",
      ];
      return canonical.filter((x) => cols.includes(x));
    }

    if (isProfilVsEvent) {
      const canonical = ["id_mt", "parameter_uji", "hasil_uji", "status_uji"];
      return canonical.filter((x) => cols.includes(x));
    }

    if (isBanProfilVsLastEvent) {
      const canonical = ["id_ban", "seri_ban", "parameter_uji", "hasil_uji", "status_uji"];
      return canonical.filter((x) => cols.includes(x));
    }

    if (isFormasiBanJanggal) {
      const canonical = ["id_mt", "nopol", "status", "roda", "cadangan", "kapasitas", "daftar_ban"];
      return canonical.filter((x) => cols.includes(x));
    }

    if (isCekValidasiOdoKmt) {
      const priority = ["CABANG", "cabang"];
      const head = priority.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !priority.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isMonitoringBan) {
      const hiddenForMonitoring = new Set(["id_mt", "id_ban"]);
      const filtered = cols.filter((x) => !hiddenForMonitoring.has(String(x || "").trim().toLowerCase()));
      const priority = ["cabang"];
      const head = priority.filter((x) => filtered.includes(x));
      const tail = filtered.filter((x) => !priority.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    if (isDashboardDaftarHadirBulanan) {
      const canonical = [
        "Cabang",
        "Tanggal",
        "Nama",
        "Status Absen",
        "Jam Absen",
        "Lokasi",
        "Foto",
        "Keterangan",
        "Supervisi",
        "Lampiran",
      ];
      const head = canonical.filter((x) => cols.includes(x));
      const tail = cols.filter((x) => !canonical.includes(x));
      return moveUserToLast([...head, ...tail]);
    }

    return moveUserToLast(cols);
  }, [baseRows, isLaporanMt, isMasterBan, isInputOdoMt, isLaporanHarian, isLaporanUpdateOdo, isLaporanEventMt, isLaporanEventBanLike, isCekValidasiOdoKmt, isJarakTempuhMt, isWearRate, isWearRateSejakAwal, isWearRateLastPosition, isWearRatePerMtPosition, isStokGudang, isStokPabrik, isBanVsFormasi, isProfilVsEvent, isBanProfilVsLastEvent, isFormasiBanJanggal, isMonitoringBan, isDashboardDaftarHadirBulanan, inputOdoPeriode]);

  const displayColumns = useMemo(
    () => allColumns.filter((col) => {
      const n = normalizeColumnName(col);
      if ((isLaporanMt || isMasterBan || isInputOdoMt || isLaporanHarian || isLaporanKoreksiOdoTd || isCekValidasiOdoKmt || isStokGudang || isJarakTempuhMt || isWearRate || isMonitoringBan || isDashboardDaftarHadirBulanan) && n === "cabang") return true;
      if (isMasterBan && n === "harga") return false;
      return !isHiddenReportColumn(col);
    }),
    [allColumns, isLaporanMt, isMasterBan, isInputOdoMt, isLaporanHarian, isLaporanKoreksiOdoTd, isCekValidasiOdoKmt, isStokGudang, isJarakTempuhMt, isWearRate, isMonitoringBan, isDashboardDaftarHadirBulanan]
  );
  const [selectedColumns, setSelectedColumns] = useState(displayColumns);
  const [draggingCol, setDraggingCol] = useState("");
  const [includeTextInput, setIncludeTextInput] = useState("");
  const [excludeTextInput, setExcludeTextInput] = useState("");
  const [includeText, setIncludeText] = useState("");
  const [excludeText, setExcludeText] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [hoveredCol, setHoveredCol] = useState("");
  const [pageSize, setPageSize] = useState("20");
  const [page, setPage] = useState(1);
  const [openHistoryMtModal, setOpenHistoryMtModal] = useState(false);
  const [historyMtKeyword, setHistoryMtKeyword] = useState("");
  const [openHistoryBanModal, setOpenHistoryBanModal] = useState(false);
  const [historyBanKeyword, setHistoryBanKeyword] = useState("");
  const [showMtDetailModal, setShowMtDetailModal] = useState(false);
  const [selectedMtDetail, setSelectedMtDetail] = useState(null);
  const [showBanDetailModal, setShowBanDetailModal] = useState(false);
  const [selectedBanId, setSelectedBanId] = useState(0);
  const [selectedBanPosisi, setSelectedBanPosisi] = useState("");
  const [openingMtDetail, setOpeningMtDetail] = useState(false);
  const [showLampiranModal, setShowLampiranModal] = useState(false);
  const [lampiranFiles, setLampiranFiles] = useState([]);
  const [lampiranTitle, setLampiranTitle] = useState("Lampiran Laporan");
  const [daftarHadirMode, setDaftarHadirMode] = useState(isDashboardDaftarHadirBulanan ? "pivot" : "table");
  const [daftarHadirSelectedMonth, setDaftarHadirSelectedMonth] = useState(() => {
    const monthToken = String(String(activeReportKey || "").split("|")[1] || "").trim();
    return /^\d{4}-\d{2}$/.test(monthToken) ? monthToken : getJakartaMonthKey();
  });
  const [daftarHadirMonthLoading, setDaftarHadirMonthLoading] = useState(false);
  const [daftarHadirMonthError, setDaftarHadirMonthError] = useState("");
  const [showRekapAbsensiModal, setShowRekapAbsensiModal] = useState(false);
  const [rekapAbsensiLoading, setRekapAbsensiLoading] = useState(false);
  const [rekapAbsensiError, setRekapAbsensiError] = useState("");
  const [rekapAbsensiRows, setRekapAbsensiRows] = useState([]);
  const [rekapAbsensiBulan, setRekapAbsensiBulan] = useState("");
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryMode, setSummaryMode] = useState("cabang");
  const summaryOptionDefs = isLaporanKoreksiOdoTd
    ? [
      { key: "cabang", label: "Cabang", candidates: ["CABANG", "cabang"] },
      { key: "user_awal", label: "User Awal", candidates: ["User Awal", "user_awal"] },
      { key: "user_akhir", label: "User Akhir", candidates: ["User Koreksi", "User Akhir", "user_koreksi", "user_akhir"] },
      { key: "jenis_koreksi", label: "Jenis Koreksi", candidates: ["Jenis Koreksi", "jenis_koreksi"] },
    ]
    : [
      { key: "cabang", label: "Cabang", candidates: ["cabang", "kode_cabang", "nama_cabang"] },
      { key: "status", label: "Status", candidates: ["status", "status_ban", "status_ganti", "status_terakhir", "status_mt", "Status Absen"] },
      { key: "kapasitas", label: "Kapasitas", candidates: ["kapasitas"] },
      { key: "kategori", label: "Kategori", candidates: ["kategori"] },
      { key: "ukuran", label: "Ukuran", candidates: ["ukuran"] },
      { key: "merk", label: "Merk", candidates: ["merk"] },
      { key: "vendor", label: "Vendor", candidates: ["suplier","vendor","id_suplier"] },
      { key: "event", label: "Event", candidates: ["event","kode_event"] },
    ];

  const opsiMtList = Array.isArray(globalOpsi?.opsiMT) ? globalOpsi.opsiMT : [];
  const nopolByIdMt = useMemo(() => {
    const map = new Map();
    opsiMtList.forEach((x) => {
      const idMt = String(x?.id_mt || "").trim().toUpperCase();
      const nopol = String(x?.nopol || "").trim();
      if (!idMt || !nopol || map.has(idMt)) return;
      map.set(idMt, nopol);
    });
    return map;
  }, [opsiMtList]);
  const idMtByNopol = useMemo(() => {
    const map = new Map();
    opsiMtList.forEach((x) => {
      const idMt = String(x?.id_mt || "").trim();
      const nopol = normalizeNopolToken(x?.nopol);
      if (!idMt || !nopol || map.has(nopol)) return;
      map.set(nopol, idMt);
    });
    return map;
  }, [opsiMtList]);
  const idBanBySeri = useMemo(() => {
    const map = new Map();
    (Array.isArray(opsiBan) ? opsiBan : []).forEach((x) => {
      const idBan = Number(x?.id_ban || 0);
      const seri = normalizeSeriToken(x?.seri_ban);
      if (!Number.isFinite(idBan) || idBan <= 0 || !seri || map.has(seri)) return;
      map.set(seri, idBan);
    });
    return map;
  }, [opsiBan]);
  const getTargetHistoryIdentity = (row, aliases) => {
    const src = row && typeof row === "object" ? row : {};
    const direct = aliases
      .map((key) => String(src?.[key] ?? "").trim())
      .find(Boolean);
    if (direct || !isHistoryLinkTargetReport) return direct || "";

    const wanted = new Set(aliases.map((key) => normalizeColumnName(key)));
    const matchedKey = Object.keys(src).find((key) => wanted.has(normalizeColumnName(key)));
    return matchedKey ? String(src?.[matchedKey] ?? "").trim() : "";
  };
  const resolveIdBanFromRow = (row) => {
    const rawId = getTargetHistoryIdentity(row, ["id_ban", "id ban"]);
    const idFromRaw = Number(rawId.replace(/[^\d]/g, ""));
    if (Number.isFinite(idFromRaw) && idFromRaw > 0) return idFromRaw;
    const seriToken = normalizeSeriToken(getTargetHistoryIdentity(row, ["seri_ban", "seri ban"]));
    const fromSeriMap = Number(idBanBySeri.get(seriToken) || 0);
    if (Number.isFinite(fromSeriMap) && fromSeriMap > 0) return fromSeriMap;
    return 0;
  };
  const normalizePerubahanValue = (colName, rawValue) => {
    if (!isPerubahanLikeColumn(colName)) return toKeyValueText(rawValue);
    const baseText = toKeyValueText(rawValue);
    if (!baseText || baseText === "-") return "-";
    const replacedHash = String(baseText).replace(/#/g, " | ");
    const replacedMt = replacedHash.replace(/\b(K-[A-Z0-9-]+)\b/gi, (match) => {
      const key = String(match || "").trim().toUpperCase();
      return nopolByIdMt.get(key) || match;
    });
    const replacedCabang = replacedMt.replace(/\b(C-[A-Z0-9-]+)\b/gi, (match) => {
      const key = String(match || "").trim().toUpperCase();
      return cabangMap.get(key) || match;
    });
    return replacedCabang;
  };

  useEffect(() => {
    setDaftarHadirMode(isDashboardDaftarHadirBulanan ? "pivot" : "table");
  }, [isDashboardDaftarHadirBulanan, activeReportKey]);
  useEffect(() => {
    if (!isDashboardDaftarHadirBulanan) return;
    const activeMonth = String(daftarHadirReportMeta?.activeMonth || "").trim();
    const fallbackMonth = daftarHadirMonthOptions[0]?.value || getJakartaMonthKey();
    setDaftarHadirSelectedMonth(activeMonth || fallbackMonth);
  }, [isDashboardDaftarHadirBulanan, daftarHadirReportMeta?.activeMonth, daftarHadirMonthOptions]);
  useEffect(() => {
    if (!isDashboardDaftarHadirBulanan) return undefined;
    const selectedMonth = String(daftarHadirSelectedMonth || "").trim();
    const activeMonth = String(daftarHadirReportMeta?.activeMonth || "").trim();
    if (!/^\d{4}-\d{2}$/.test(selectedMonth) || selectedMonth === activeMonth) return undefined;

    let isMounted = true;
    setDaftarHadirMonthLoading(true);
    setDaftarHadirMonthError("");

    api.get(`${backendPort}/api/main/dashboard_daftar_hadir_bulanan`, {
      params: {
        kode_cabang: daftarHadirReportMeta?.kodeCabangParam || undefined,
        bulan: selectedMonth,
      },
      requestGroup: REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN_PAGE34,
      timeout: 120000,
    }).then(({ data }) => {
      if (!isMounted || typeof window === "undefined") return;
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      const prev = (window.__laporan_runtime_state && typeof window.__laporan_runtime_state === "object")
        ? window.__laporan_runtime_state
        : {};
      const reportKey = `DASHBOARD_DAFTAR_HADIR_BULANAN|${selectedMonth}|${daftarHadirReportMeta?.kodeCabangRaw || "ALL"}`;
      window.__laporan_runtime_state = {
        ...prev,
        viewMeta: {
          title: `ABSENSI BULANAN - ${daftarHadirReportMeta?.namaCabang || "Semua Cabang"}`,
          tanggal: formatMonthLabel(selectedMonth),
          report_key: reportKey,
        },
        cacheMap: {
          ...(prev?.cacheMap && typeof prev.cacheMap === "object" ? prev.cacheMap : {}),
          [reportKey]: { rows },
        },
      };
      setRuntimeTick((tick) => tick + 1);
    }).catch((err) => {
      if (!isMounted || api.isCanceledError?.(err)) return;
      console.error("Gagal load dashboard_daftar_hadir_bulanan dari Page34:", err);
      setDaftarHadirMonthError(err?.response?.data?.message || "Gagal memuat absensi bulanan.");
    }).finally(() => {
      if (isMounted) setDaftarHadirMonthLoading(false);
    });

    return () => {
      isMounted = false;
      api.cancelPendingRequests?.({
        group: REQ_GROUP_DASHBOARD_DAFTAR_HADIR_BULANAN_PAGE34,
        reason: "Page34 daftar hadir bulanan selector change",
      });
    };
  }, [
    isDashboardDaftarHadirBulanan,
    daftarHadirSelectedMonth,
    daftarHadirReportMeta?.activeMonth,
    daftarHadirReportMeta?.kodeCabangParam,
    daftarHadirReportMeta?.kodeCabangRaw,
    daftarHadirReportMeta?.namaCabang,
  ]);
  const openMtDetailFromNopol = async (row) => {
    if (openingMtDetail) return;
    const rawNopol = String(row?.nopol || "").trim();
    if (!rawNopol || rawNopol === "-") return;
    const tokenNopol = normalizeNopolToken(rawNopol);
    let idMt = String(idMtByNopol.get(tokenNopol) || "").trim();
    if (!idMt) {
      const match = opsiMtList.find((x) => normalizeNopolToken(x?.nopol) === tokenNopol);
      idMt = String(match?.id_mt || "").trim();
    }
    if (!idMt) return;
    const mtSource = opsiMtList.find((x) => String(x?.id_mt || "").trim() === idMt) || null;
    setOpeningMtDetail(true);
    try {
      const payload = {
        page: 1,
        pageSize: 20,
        kodeCabang: "",
        searchBy: "id_mt",
        keyword: idMt,
        sortBy: "id_mt",
        sortDir: "asc",
      };
      let mtRowDetail = null;
      try {
        const { data } = await api.post(`${backendPort}/api/main/load_page9_mt_list`, payload, { timeout: 20000 });
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        mtRowDetail = rows.find((x) => String(x?.id_mt || "").trim() === idMt) || null;
      } catch (_err) {}
      if (!mtRowDetail) return;
      const editDataFromApi = {
        id_mt: String(mtRowDetail?.id_mt || idMt).trim(),
        nopol: String(mtRowDetail?.nopol || rawNopol).trim(),
        kode_cabang: String(mtRowDetail?.kode_cabang_aktif || mtRowDetail?.kode_cabang || "").trim(),
        merk: String(mtRowDetail?.merk || "").trim(),
        kapasitas: String(mtRowDetail?.kapasitas || "").trim(),
        kategori: String(mtRowDetail?.kategori || "").trim(),
        status: String(mtRowDetail?.status || "").trim(),
        roda: Number(mtRowDetail?.roda || 0),
        cadangan: Number(mtRowDetail?.cadangan || 0),
        tahun_pembuatan: String(mtRowDetail?.tahun_pembuatan || "").trim(),
        kir: String(mtRowDetail?.kir || "").trim(),
        odo_awal: Number(mtRowDetail?.odo_awal || 0),
        catatan_ban_initial: String(mtRowDetail?.catatan_ban_initial || "").trim(),
        tanggal: mtRowDetail?.tanggal || null,
        user: String(mtRowDetail?.user || "").trim(),
        lampiran: Array.isArray(mtRowDetail?.lampiran)
          ? mtRowDetail.lampiran
          : (() => {
              try {
                const parsed = JSON.parse(String(mtRowDetail?.lampiran || "[]"));
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            })(),
        lampiran_mt_event: String(mtRowDetail?.lampiran_mt_event || ""),
      };
      setSelectedMtDetail(editDataFromApi);
      setShowMtDetailModal(true);
    } finally {
      setOpeningMtDetail(false);
    }
  };
  const openHistoryMtFromNopol = (row) => {
    const rawNopol = getTargetHistoryIdentity(row, ["nopol", "no_pol", "no pol"]);
    if (!rawNopol || rawNopol === "-") return;
    const tokenNopol = normalizeNopolToken(rawNopol);
    const idMtFromMap = String(idMtByNopol.get(tokenNopol) || "").trim();
    const rawIdMt = getTargetHistoryIdentity(row, ["id_mt", "id mt"]);
    const keyword = idMtFromMap || rawIdMt || rawNopol;
    if (!keyword) return;
    setHistoryMtKeyword(keyword);
    setOpenHistoryMtModal(true);
  };
  const openBanDetailFromSeri = (row) => {
    const rawSeri = String(row?.seri_ban || "").trim();
    if (!rawSeri || rawSeri === "-") return;
    const idBan = resolveIdBanFromRow(row);
    if (!Number.isFinite(idBan) || idBan <= 0) return;
    setSelectedBanId(idBan);
    setSelectedBanPosisi(String(row?.posisi || "").trim());
    setShowBanDetailModal(true);
  };

  useEffect(() => {
    setSelectedColumns((prev) => {
      if (isDashboardDaftarHadirBulanan) return displayColumns;
      if (!Array.isArray(prev) || prev.length === 0) return displayColumns;
      const prevSet = new Set(prev);
      const kept = displayColumns.filter((col) => prevSet.has(col));
      const added = displayColumns.filter((col) => !prevSet.has(col));
      return [...kept, ...added];
    });
  }, [displayColumns, activeReportKey, isDashboardDaftarHadirBulanan]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIncludeText(includeTextInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [includeTextInput]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setExcludeText(excludeTextInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [excludeTextInput]);

  const syncFromTop = (e) => {
    if (!bottomScrollRef.current) return;
    bottomScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  const syncFromBottom = (e) => {
    if (!topScrollRef.current) return;
    topScrollRef.current.scrollLeft = e.target.scrollLeft;
  };

  const filteredRows = useMemo(() => {
    const inc = normalize(includeText).trim();
    const exc = normalize(excludeText).trim();
    return baseRows.filter((row) => {
      const line = normalize(Object.values(row || {}).join(" "));
      if (inc && !line.includes(inc)) return false;
      if (exc && line.includes(exc)) return false;
      return true;
    });
  }, [baseRows, includeText, excludeText]);

  const daftarHadirPivotMeta = useMemo(() => {
    if (!isDashboardDaftarHadirBulanan) return { dayColumns: [], todayKey: "", monthKey: "", lastDay: 0 };
    const parts = String(activeReportKey || "").split("|");
    const monthKey = /^\d{4}-\d{2}$/.test(parts[1] || "") ? parts[1] : "";
    if (!monthKey) return { dayColumns: [], todayKey: getJakartaTodayKey(), monthKey: "", lastDay: 0 };
    const [year, month] = monthKey.split("-").map((x) => Number(x));
    const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const dayColumns = Array.from({ length: lastDay }, (_x, idx) => String(idx + 1));
    return { dayColumns, todayKey: getJakartaTodayKey(), monthKey, lastDay };
  }, [isDashboardDaftarHadirBulanan, activeReportKey]);

  const pivotRowsRaw = useMemo(() => {
    if (!isDashboardDaftarHadirBulanan) return [];
    const monthKey = String(daftarHadirPivotMeta?.monthKey || "").trim();
    const dayColumns = Array.isArray(daftarHadirPivotMeta?.dayColumns) ? daftarHadirPivotMeta.dayColumns : [];
    const map = new Map();

    baseRows.forEach((row) => {
      const cabang = String(row?.Cabang ?? row?.cabang ?? "-").trim() || "-";
      const nama = String(row?.Nama ?? row?.nama ?? "-").trim() || "-";
      const tanggalKey = dateKeyToJakarta(row?.Tanggal ?? row?.tanggal ?? "");
      const dayToken = tanggalKey.startsWith(`${monthKey}-`) ? String(Number(tanggalKey.slice(-2))) : "";
      if (!dayToken || !dayColumns.includes(dayToken)) return;
      const statusAbsen = String(row?.["Status Absen"] ?? row?.status_absen ?? row?.statusAbsen ?? "BELUM ABSEN").trim() || "BELUM ABSEN";
      const jamAbsen = String(row?.["Jam Absen"] ?? row?.jam_absen ?? row?.jamAbsen ?? "-").trim() || "-";
      const key = `${cabang}|||${nama}`;
      const current = map.get(key) || (() => {
        const seed = { CABANG: cabang, "NAMA PEGAWAI": nama };
        dayColumns.forEach((day) => {
          seed[day] = "-";
          seed[`__status_${day}`] = "";
        });
        return seed;
      })();
      const prevValue = String(current?.[dayToken] ?? "").trim();
      const prevStatus = String(current?.[`__status_${dayToken}`] ?? "").trim();
      const nextHasJam = jamAbsen && jamAbsen !== "-";
      const prevHasJam = prevValue && prevValue !== "-";
      const shouldReplace = !prevHasJam || (nextHasJam && !prevHasJam) || (prevStatus === "BELUM ABSEN" && statusAbsen !== "BELUM ABSEN");
      if (shouldReplace) {
        current[dayToken] = jamAbsen || "-";
        current[`__status_${dayToken}`] = statusAbsen;
      }
      map.set(key, current);
    });

    return Array.from(map.values()).sort((a, b) => {
      const cabangCmp = String(a?.CABANG || "").localeCompare(String(b?.CABANG || ""), "id", { sensitivity: "base", numeric: true });
      if (cabangCmp !== 0) return cabangCmp;
      return String(a?.["NAMA PEGAWAI"] || "").localeCompare(String(b?.["NAMA PEGAWAI"] || ""), "id", { sensitivity: "base", numeric: true });
    });
  }, [isDashboardDaftarHadirBulanan, baseRows, daftarHadirPivotMeta]);

  const filteredPivotRows = useMemo(() => {
    if (!isDashboardDaftarHadirBulanan) return [];
    const inc = normalize(includeText).trim();
    const exc = normalize(excludeText).trim();
    const filtered = pivotRowsRaw.filter((row) => {
      const line = normalize(Object.entries(row || {})
        .filter(([key]) => !String(key || "").startsWith("__status_"))
        .map(([_key, value]) => String(value ?? ""))
        .join(" "));
      if (inc && !line.includes(inc)) return false;
      if (exc && line.includes(exc)) return false;
      return true;
    });
    if (!sortBy) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = String(a?.[sortBy] ?? "");
      const bv = String(b?.[sortBy] ?? "");
      const cmp = av.localeCompare(bv, "id", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [isDashboardDaftarHadirBulanan, pivotRowsRaw, includeText, excludeText, sortBy, sortDir]);

  const sortedRows = useMemo(() => {
    if (!sortBy) return filteredRows;
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const av = String(a?.[sortBy] ?? "");
      const bv = String(b?.[sortBy] ?? "");
      const cmp = av.localeCompare(bv, "id", { numeric: true, sensitivity: "base" });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filteredRows, sortBy, sortDir]);

  const summaryRows = useMemo(() => {
    const candidates = (summaryOptionDefs.find((x) => x.key === summaryMode)?.candidates) || [];
    const counter = new Map();
    const srcRows = Array.isArray(sortedRows) ? sortedRows : [];
    srcRows.forEach((row) => {
      const src = row && typeof row === "object" ? row : {};
      let picked = "";
      for (const key of candidates) {
        const raw = String(src?.[key] ?? "").trim();
        if (raw) {
          picked = raw;
          break;
        }
      }
      const label = picked || "-";
      counter.set(label, Number(counter.get(label) || 0) + 1);
    });
    return Array.from(counter.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), "id", { sensitivity: "base", numeric: true }));
  }, [sortedRows, summaryMode]);

  const availableSummaryOptions = useMemo(() => {
    return summaryOptionDefs.filter((opt) => opt.candidates.some((candidate) => displayColumns.includes(candidate)));
  }, [displayColumns]);

  useEffect(() => {
    if (!availableSummaryOptions.length) return;
    if (!availableSummaryOptions.some((x) => x.key === summaryMode)) {
      setSummaryMode(availableSummaryOptions[0].key);
    }
  }, [availableSummaryOptions, summaryMode]);

  const isPivotMode = isDashboardDaftarHadirBulanan && daftarHadirMode === "pivot";
  const isDaftarHadirTableMode = isDashboardDaftarHadirBulanan && !isPivotMode;
  const activeRows = isPivotMode ? filteredPivotRows : sortedRows;
  const totalRows = activeRows.length;
  const loadedRowsCount = isPivotMode ? pivotRowsRaw.length : baseRows.length;
  const pageSizeOptions = loadedRowsCount <= 1000
    ? ["20", "50", "100", "All"]
    : ["20", "50", "100", "1000"];
  const effectivePageSize = pageSizeOptions.includes(pageSize) ? pageSize : pageSizeOptions[0];
  const perPage = effectivePageSize === "All" ? totalRows || 1 : Number(effectivePageSize);
  const totalPages = effectivePageSize === "All" ? 1 : Math.max(1, Math.ceil(totalRows / perPage));
  const safePage = Math.min(page, totalPages);
  const start = effectivePageSize === "All" ? 0 : (safePage - 1) * perPage;
  const end = effectivePageSize === "All" ? totalRows : start + perPage;
  const pagedRows = activeRows.slice(start, end);
  const isInputOdoFitMode = isInputOdoMt && !isPivotMode;
  const inputOdoDayColumnCount = selectedColumns.filter((col) => isInputOdoDayColumn(col)).length;
  const getInputOdoColumnWidth = (col) => {
    const n = normalizeColumnName(col);
    if (isInputOdoDayColumn(col)) {
      const reserved = 28;
      const available = Math.max(45, 100 - reserved);
      const pct = available / Math.max(1, inputOdoDayColumnCount);
      return `${pct.toFixed(3)}%`;
    }
    if (n === "cabang") return "7%";
    if (n === "periode") return "5%";
    if (n === "nopol") return "6%";
    if (n === "status akhir") return "6%";
    if (n === "user") return "4%";
    return "4%";
  };
  const inputOdoNoWidth = "3%";
  const inputOdoBaseCellStyle = {
    padding: "0.1rem 0.04rem",
    fontSize: "9px",
    lineHeight: 1.1,
    verticalAlign: "middle",
  };

  useEffect(() => {
    if (pageSizeOptions.includes(pageSize)) return;
    setPageSize(pageSizeOptions[0]);
    setPage(1);
  }, [pageSize, pageSizeOptions]);

  useEffect(() => {
    const updateWidth = () => {
      const nextWidth = Number(tableRef.current?.scrollWidth || 0);
      setScrollWidth(nextWidth);
    };
    updateWidth();
    const ro = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => updateWidth())
      : null;
    if (ro && tableRef.current) ro.observe(tableRef.current);
    window.addEventListener("resize", updateWidth);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [selectedColumns, activeReportKey, includeText, excludeText, sortBy, sortDir, pageSize, page, isPivotMode, daftarHadirPivotMeta.dayColumns.length, pagedRows.length]);

  const toggleColumn = (name) => {
    setSelectedColumns((prev) => {
      if (prev.includes(name)) return prev.filter((x) => x !== name);
      return [...prev, name];
    });
  };

  const moveColumn = (fromCol, toCol) => {
    if (!fromCol || !toCol || fromCol === toCol) return;
    setSelectedColumns((prev) => {
      const fromIdx = prev.indexOf(fromCol);
      const toIdx = prev.indexOf(toCol);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const next = [...prev];
      const [picked] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, picked);
      return next;
    });
  };

  const handleSort = (col) => {
    if (sortBy !== col) {
      setSortBy(col);
      setSortDir("asc");
      return;
    }
    setSortDir((d) => (d === "asc" ? "desc" : "asc"));
  };

  const buildExcelHtml = ({ headers, rows }) => {
    if (!headers.length) {
      alert("Tidak ada kolom yang dipilih.");
      return "";
    }
    const escapeHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;");
    const headHtml = headers
      .map(
        (h) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeHtml(h)}</th>`
      )
      .join("");
    const transactionLegend = isLaporanUpdateOdo ? UPDATE_ODO_TRANSACTION_LEGEND : INPUT_ODO_LEGEND;
    const inputOdoLegendHtml = (isInputOdoMt || isLaporanUpdateOdo)
      ? (() => {
        const headerCount = Math.max(1, headers.length);
        const legendCells = transactionLegend.map((item) => {
          const bg = normalizeExcelColor(item.background, "#FFFFFF");
          const color = normalizeExcelColor(item.color, "#000000");
          return `<td style="background-color:${bg};color:${color};text-align:center;font-weight:600;mso-pattern:auto solid ${bg};">${escapeHtml(item.label)}</td>`;
        }).join("");
        const restCount = Math.max(0, headerCount - transactionLegend.length);
        const restCells = restCount > 0 ? `<td colspan="${restCount}"></td>` : "";
        return `<tr><td colspan="${headerCount}" style="font-weight:600;">Keterangan</td></tr><tr>${legendCells}${restCells}</tr><tr><td colspan="${headerCount}"></td></tr>`;
      })()
      : "";
    const bodyHtml = rows
      .map((r) => `<tr>${headers.map((h) => {
        const colName = String(h || "");
        const isOdoDayCol = isInputOdoMt && isInputOdoDayColumn(colName);
        const isUpdateOdoValueCol = isLaporanUpdateOdo && normalizeColumnName(colName) === "odo";
        const isUpdateOdoTransactionCol = isLaporanUpdateOdo && normalizeColumnName(colName) === "transaksi";
        const isStokRightCol = isStokGudang && isStokGudangRightColumn(colName);
        const isWearRateCol = isWearRate && normalizeColumnName(colName) === "wear rate";
        const isWearRatePerMtPositionDateCol = isWearRatePerMtPosition && isWearRatePerMtPositionDateColumn(colName);
        const isWearRateLastPositionNumericCol = isWearRateLastPosition && isWearRateLastPositionNumericColumn(colName);
        const isWearRateTdCorrectionWarning = ((isWearRateLastPosition && normalizeColumnName(colName) === "wear rate di posisi")
          || (isWearRateSejakAwal && normalizeColumnName(colName) === "wear rate")
          || (isWearRatePerMtPosition && normalizeColumnName(colName) === "wear rate posisi"))
          && hasWearRateTdCorrectionWarning(r);
        const isWearRatePerMtPositionNumericCol = isWearRatePerMtPosition && isWearRatePerMtPositionNumericColumn(colName);
        const isEventMtNumericCol = isLaporanEventMt && isEventMtRightNumberColumn(colName);
        const isEventBanKmtSaatEventCol = isLaporanEventBanLike && isEventBanKmtSaatEventColumn(colName);
        const isEventBanTdCol = isLaporanEventBanLike && isEventBanTdColumn(colName);
        const isBanVsCenterCol = isBanVsFormasi && isBanVsFormasiCenterColumn(colName);
        const isWearRateSejakAwalNumericCol = isWearRateSejakAwal && isWearRateSejakAwalNumericColumn(colName);
        const isLampiranCol = isLampiranColumn(colName);
        const val = isWearRatePerMtPositionDateCol
          ? formatWearRatePerMtPositionDate(r?.[h])
          : (isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol
          ? formatThousandsDot(r?.[h])
          : isWearRateCol
          ? formatDecimalId(r?.[h], 2)
          : (isLampiranCol
          ? formatLampiranSummary(r?.[h])
          : ((isThousandDotColumn(colName) || isOdoDayCol || isUpdateOdoValueCol || isStokRightCol || isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol || isWearRatePerMtPositionNumericCol || isEventMtNumericCol || isEventBanKmtSaatEventCol)
          ? formatThousandsDot(r?.[h])
          : normalizePerubahanValue(colName, r?.[h]))));
        const posisiState = getPosisiVisualState(r, colName);
        const odoState = getInputOdoVisualState(r, colName, isInputOdoMt);
        const odoStyle = getInputOdoCellStyle(odoState);
        const updateOdoTransactionStyle = getInputOdoCellStyle(getUpdateOdoTransactionState(r?.[h]));
        const bg = isWearRateTdCorrectionWarning
          ? "background:#fecaca;color:#991b1b;font-weight:600;"
          : (isUpdateOdoTransactionCol
          ? `background:${updateOdoTransactionStyle.background};color:${updateOdoTransactionStyle.color};`
          : (isOdoDayCol
          ? `background:${odoStyle.background};color:${odoStyle.color};`
          : (posisiState === "inactive"
          ? "background:#e5e7eb;"
          : (posisiState === "missing" || odoState === "missing")
            ? "background:#fbcfe8;"
            : "")));
        const pad = isKmtLaporanColumn(colName) ? "padding-left:20px;padding-right:20px;" : "";
        const jarakPad = isJarakTempuhPadColumn(colName) ? "padding-right:20px;" : "";
        const wearRatePad = isWearRateNumericPadColumn(colName) ? "padding-right:20px;" : "";
        const stokPad = isStokRightCol ? "padding-right:30px;" : "";
        const align = (isBanVsCenterCol || isEventBanTdCol)
          ? "text-align:center;"
          : (isTdLaporanColumn(colName)
          ? "text-align:center;"
          : ((isThousandDotColumn(colName) || isOdoDayCol || isUpdateOdoValueCol || isStokRightCol || isWearRateCol || isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol || isWearRatePerMtPositionNumericCol || isEventMtNumericCol || isEventBanKmtSaatEventCol) ? "text-align:right;" : ""));
        return `<td style="${bg}${align}${pad}${jarakPad}${wearRatePad}${stokPad}">${escapeHtml(val)}</td>`;
      }).join("")}</tr>`)
      .join("");
    return `\uFEFF<html><head><meta charset=\"UTF-8\"></head><body><table border=\"1\"><thead>${inputOdoLegendHtml}<tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  };

  const buildAbsensiPivotExcelHtml = ({ rows }) => {
    const dayColumns = Array.isArray(daftarHadirPivotMeta?.dayColumns) ? daftarHadirPivotMeta.dayColumns : [];
    const monthKey = String(daftarHadirPivotMeta?.monthKey || "").trim();
    const todayKey = String(daftarHadirPivotMeta?.todayKey || "").trim();
    const headers = ["CABANG", "NAMA PEGAWAI", ...dayColumns];
    const escapeHtml = (v) =>
      String(v ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\"/g, "&quot;");
    const headHtml = headers
      .map((h) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeHtml(h)}</th>`)
      .join("");
    const bodyHtml = (Array.isArray(rows) ? rows : [])
      .map((row) => {
        const tdHtml = headers.map((header) => {
          if (header === "CABANG" || header === "NAMA PEGAWAI") {
            return `<td>${escapeHtml(row?.[header] ?? "-")}</td>`;
          }
          const day = String(header || "").trim();
          const dayKey = `${monthKey}-${day.padStart(2, "0")}`;
          const status = String(row?.[`__status_${day}`] ?? "").trim();
          const value = String(row?.[day] ?? "-").trim() || "-";
          const isFutureDate = Boolean(todayKey && dayKey > todayKey);
          const style = buildAbsensiStatusStyle(status, isFutureDate);
          const excelBg = normalizeExcelColor(style.background, "#FFFFFF");
          const excelColor = normalizeExcelColor(style.color, "#000000");
          return `<td style="background-color:${excelBg};color:${excelColor};text-align:center;mso-pattern:auto solid ${excelBg};">${escapeHtml(value)}</td>`;
        }).join("");
        return `<tr>${tdHtml}</tr>`;
      })
      .join("");
    return `\uFEFF<html><head><meta charset=\"UTF-8\"></head><body><table border=\"1\"><thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
  };

  const downloadExcelBlob = ({ headers, rows, fileSuffix = "preview" }) => {
    const html = isPivotMode
      ? buildAbsensiPivotExcelHtml({ rows })
      : buildExcelHtml({ headers, rows });
    if (!html) return;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${String(viewMeta?.title || "laporan").replace(/\\s+/g, "_")}_${String(viewMeta?.tanggal || "tanggal")}_${fileSuffix}.xls`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const downloadPreview = () => {
    downloadExcelBlob({
      headers: isPivotMode ? ["CABANG", "NAMA PEGAWAI", ...daftarHadirPivotMeta.dayColumns] : selectedColumns,
      rows: isPivotMode ? pagedRows : sortedRows,
      fileSuffix: "preview",
    });
  };

  const downloadOdoAttachments = async () => {
    if (!isLaporanUpdateOdo || isDownloadingOdoAttachments) return;
    const meta = activePayload?.meta && typeof activePayload.meta === "object" ? activePayload.meta : {};
    const kodeCabang = String(meta?.kode_cabang || "").trim();
    const awalPeriode = String(meta?.awal_periode || "").trim();
    const akhirPeriode = String(meta?.akhir_periode || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(awalPeriode) || !/^\d{4}-\d{2}-\d{2}$/.test(akhirPeriode)) {
      alert("Periode Laporan Update ODO tidak valid. Silakan generate laporan kembali.");
      return;
    }

    setIsDownloadingOdoAttachments(true);
    try {
      const response = await api.post(`${backendPort}/api/main/download_lampiran_laporan_update_odo`, {
        kode_cabang: kodeCabang,
        awal_periode: awalPeriode,
        akhir_periode: akhirPeriode,
      }, {
        responseType: "blob",
        timeout: 210000,
      });
      const disposition = String(response.headers?.["content-disposition"] || "");
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || `Lampiran_Update_ODO_${awalPeriode}_sd_${akhirPeriode}.zip`;
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        let message = "Gagal mengunduh lampiran Update ODO.";
        if (err?.response?.data instanceof Blob) {
          try {
            const body = JSON.parse(await err.response.data.text());
            message = body?.message || message;
          } catch {
            // Pertahankan pesan umum bila respons gagal dibaca sebagai JSON.
          }
        } else {
          message = err?.response?.data?.message || message;
        }
        alert(message);
      }
    } finally {
      setIsDownloadingOdoAttachments(false);
    }
  };

  const downloadAll = () => {
    downloadExcelBlob({
      headers: isPivotMode ? ["CABANG", "NAMA PEGAWAI", ...daftarHadirPivotMeta.dayColumns] : displayColumns,
      rows: isPivotMode ? pivotRowsRaw : baseRows,
      fileSuffix: "all",
    });
  };

  const rekapAbsensiHeaders = [
    { key: "no", label: "NO." },
    { key: "nama_pegawai", label: "NAMA PEGAWAI" },
    { key: "cabang", label: "CABANG" },
    { key: "bulan", label: "BULAN" },
    { key: "hadir", label: "HADIR" },
    { key: "libur", label: "LIBUR" },
    { key: "ijin", label: "IJIN" },
    { key: "sakit", label: "SAKIT" },
    { key: "cuti", label: "CUTI" },
    { key: "dinas_luar", label: "DINAS LUAR" },
    { key: "alpa", label: "ALPA" },
    { key: "non_aktif", label: "NON AKTIF" },
    { key: "lainnya", label: "LAINNYA" },
    { key: "belum_absen", label: "BELUM ABSEN" },
    { key: "total_hari", label: "TOTAL HARI" },
  ];
  const rekapAbsensiPeriodLabel = formatMonthLabel(rekapAbsensiBulan || daftarHadirSelectedMonth || getJakartaMonthKey()).replace(/\s+/g, "-");
  const rekapAbsensiDisplayRows = useMemo(() => (
    (Array.isArray(rekapAbsensiRows) ? rekapAbsensiRows : []).map((row, index) => ({
      no: index + 1,
      nama_pegawai: String(row?.nama_pegawai || "-").trim() || "-",
      cabang: String(row?.cabang || "-").trim() || "-",
      bulan: rekapAbsensiPeriodLabel,
      hadir: Number(row?.hadir || 0) || "-",
      libur: Number(row?.libur || 0) || "-",
      ijin: Number(row?.ijin || 0) || "-",
      sakit: Number(row?.sakit || 0) || "-",
      cuti: Number(row?.cuti || 0) || "-",
      dinas_luar: Number(row?.dinas_luar || 0) || "-",
      alpa: Number(row?.alpa || 0) || "-",
      non_aktif: Number(row?.non_aktif || 0) || "-",
      lainnya: Number(row?.lainnya || 0) || "-",
      belum_absen: Number(row?.belum_absen || 0) || "-",
      total_hari: Number(row?.total_hari || 0) || "-",
    }))
  ), [rekapAbsensiRows, rekapAbsensiPeriodLabel]);
  const handleOpenRekapAbsensi = async () => {
    if (rekapAbsensiLoading) return;
    const bulan = String(daftarHadirSelectedMonth || daftarHadirReportMeta?.activeMonth || "").trim();
    if (!/^\d{4}-\d{2}$/.test(bulan)) return;
    setRekapAbsensiLoading(true);
    setRekapAbsensiError("");
    try {
      const { data } = await api.get(`${backendPort}/api/main/dashboard_rekap_absensi_pegawai`, {
        params: {
          kode_cabang: daftarHadirReportMeta?.kodeCabangParam || undefined,
          bulan,
        },
        requestGroup: REQ_GROUP_DASHBOARD_REKAP_ABSENSI_PEGAWAI,
        timeout: 120000,
      });
      setRekapAbsensiRows(Array.isArray(data?.rows) ? data.rows : []);
      setRekapAbsensiBulan(String(data?.bulan || bulan).trim());
      setShowRekapAbsensiModal(true);
    } catch (err) {
      if (!api.isCanceledError?.(err)) {
        console.error("Gagal memuat rekapitulasi absensi pegawai:", err);
        setRekapAbsensiError(err?.response?.data?.message || "Gagal memuat rekapitulasi absensi pegawai.");
      }
    } finally {
      setRekapAbsensiLoading(false);
    }
  };
  const downloadRekapAbsensi = () => {
    const escapeHtml = (value) => String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;");
    const totalColumns = rekapAbsensiHeaders.length;
    const headerHtml = rekapAbsensiHeaders
      .map((header) => `<th style="background:#0f2d5c;color:#ffffff;font-weight:400;">${escapeHtml(header.label)}</th>`)
      .join("");
    const bodyHtml = rekapAbsensiDisplayRows.map((row) => (
      `<tr>${rekapAbsensiHeaders.map((header) => {
        const numeric = ["no", "hadir", "libur", "ijin", "sakit", "cuti", "dinas_luar", "alpa", "non_aktif", "lainnya", "belum_absen", "total_hari"].includes(header.key);
        return `<td style="${numeric ? "text-align:right;" : ""}">${escapeHtml(row?.[header.key] ?? "")}</td>`;
      }).join("")}</tr>`
    )).join("");
    const html = `\uFEFF<html><head><meta charset="UTF-8"></head><body><table border="1"><thead><tr><th colspan="${totalColumns}" style="font-size:16pt;font-weight:700;text-align:center;">REKAPITULASI ABSENSI PEGAWAI</th></tr><tr><th colspan="${totalColumns}" style="font-weight:600;text-align:center;">Periode ${escapeHtml(rekapAbsensiPeriodLabel)}</th></tr><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `REKAPITULASI_ABSENSI_PEGAWAI_${rekapAbsensiPeriodLabel}.xls`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const getHeaderLabel = (col) => {
    const n = normalizeColumnName(col);
    if ((isLaporanMt || isMasterBan || isInputOdoMt || isStokGudang || isJarakTempuhMt || isWearRate || isMonitoringBan) && n === "cabang") return "NAMA CABANG";
    if (isMasterBan && n === "catatan ban") return "CATATAN BAN";
    return formatHeaderLabel(col);
  };
  const getDaftarHadirTableWidth = (col) => {
    const n = normalizeColumnName(col);
    if (n === "cabang") return 60;
    if (n === "tanggal") return 80;
    if (n === "nama") return 92;
    if (n === "status absen") return 78;
    if (n === "jam absen") return 58;
    if (n === "lokasi") return 120;
    if (n === "foto") return 74;
    if (n === "keterangan") return 100;
    if (n === "supervisi") return 84;
    if (n === "lampiran") return 92;
    return 90;
  };

  return (
    <>
      <div className="col-md-12 d-flex justify-content-between">
        <div className="col-md-10">
          <h1 className="text-2xl font-semibold">{viewMeta?.title || "LAPORAN"}</h1>
          {isDashboardDaftarHadirBulanan ? (
            <div className="d-flex flex-wrap align-items-center gap-2 text-gray-500 text-sm">
              <span>Tanggal laporan:</span>
              <select
                className="form-select form-select-sm"
                value={daftarHadirSelectedMonth}
                onChange={(e) => setDaftarHadirSelectedMonth(String(e.target.value || ""))}
                disabled={daftarHadirMonthLoading}
                style={{ width: 220 }}
                aria-label="Pilih periode absensi bulanan"
              >
                {daftarHadirMonthOptions.map((opt) => (
                  <option key={`absensi-bulanan-${opt.value}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span>{daftarHadirMonthLoading ? "Memuat..." : ""}</span>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">{`Tanggal Laporan: ${viewMeta?.tanggal || "-"}`}</p>
          )}
          {isDashboardDaftarHadirBulanan && daftarHadirMonthError ? (
            <div className="text-danger small mt-1">{daftarHadirMonthError}</div>
          ) : null}
        </div>
      </div>

      <section className="bg-white rounded-lg p-4 border">
        <div className="d-flex justify-content-between align-items-center gap-2 mb-3 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            {isDashboardDaftarHadirBulanan && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={() => setDaftarHadirMode((prev) => (prev === "pivot" ? "table" : "pivot"))}
              >
                {`SWITCH MODE: ${isPivotMode ? "MODEL 1" : "MODEL 2"}`}
              </button>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 flex-wrap">
          {isLaporanUpdateOdo && (
            <button
              type="button"
              className="btn btn-sm"
              onClick={downloadOdoAttachments}
              disabled={isDownloadingOdoAttachments}
              style={{ backgroundColor: "#f97316", borderColor: "#f97316", color: "#ffffff" }}
            >
              {isDownloadingOdoAttachments ? "Menyiapkan Lampiran..." : "Download Lampiran"}
            </button>
          )}
          <button type="button" className="btn btn-success btn-sm" onClick={downloadPreview}>
            Download Preview
          </button>
          <button type="button" className="btn btn-danger btn-sm" onClick={downloadAll}>
            Download All
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => typeof navigatePage === "function" && navigatePage("Page33")}>
            Close
          </button>
          </div>
        </div>
        <div className="row g-2 mb-3">
          <div className="col-md-3">
              <label className="form-label mb-1">Filter Mengandung Teks</label>
              <input
                className="form-control form-control-sm"
                value={includeTextInput}
                onChange={(e) => {
                  setIncludeTextInput(e.target.value);
                }}
                placeholder="baris mengandung teks.."
              />
          </div>
          <div className="col-md-3">
              <label className="form-label mb-1">Filter Negasi Teks</label>
              <input
                className="form-control form-control-sm"
                value={excludeTextInput}
                onChange={(e) => {
                  setExcludeTextInput(e.target.value);
                }}
                placeholder="baris tidak mengandung teks.."
              />
          </div>
          <div className="col-md-3">
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1">{`Ditampilkan ${pagedRows.length} | Total: ${totalRows}`}</label>
            <select
              className="form-select form-select-sm"
              value={effectivePageSize}
              onChange={(e) => {
                setPageSize(e.target.value);
                setPage(1);
              }}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
        </div>

        {isPivotMode && (
          <div className="mb-3 d-flex justify-content-between align-items-center gap-2 flex-wrap">
            <div className="d-flex flex-wrap gap-2 small">
              {[
                { label: "Hadir", background: "#000670f2", color: "#ffffff" },
                { label: "Libur", background: "#4a8a00", color: "#ffffff" },
                { label: "Ijin", background: "#007a82", color: "#ffffff" },
                { label: "Sakit", background: "#6e006a", color: "#ffffff" },
                { label: "Cuti", background: "#1b5e00", color: "#ffffff" },
                { label: "Dinas Luar", background: "#fbee00", color: "#000000" },
                { label: "Alpa", background: "#dd0000", color: "#ffffff" },
                { label: "Non Aktif", background: "#fb9200", color: "#000000" },
                { label: "Belum Absen", background: "#E5E7EB", color: "#475569" },
              ].map((item) => (
                <span
                  key={item.label}
                  className="px-2 py-1 border rounded"
                  style={{ background: item.background, color: item.color }}
                >
                  {item.label}
                </span>
              ))}
            </div>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={handleOpenRekapAbsensi}
              disabled={rekapAbsensiLoading}
            >
              {rekapAbsensiLoading ? "Memuat..." : "REKAP ABSEN"}
            </button>
          </div>
        )}

        {(isInputOdoMt || isLaporanUpdateOdo) && (
          <div className="mb-3 d-flex flex-wrap align-items-center gap-2 small">
            <span className="fw-semibold">Keterangan:</span>
            {(isLaporanUpdateOdo ? UPDATE_ODO_TRANSACTION_LEGEND : INPUT_ODO_LEGEND).map((item) => (
              <span
                key={`input-odo-legend-${item.label}`}
                className="px-2 py-1 border rounded"
                style={{ background: item.background, color: item.color }}
              >
                {item.label}
              </span>
            ))}
          </div>
        )}

        {!isPivotMode && (
        <div className="mb-3 p-2 border rounded" style={{ maxHeight: 130, overflowY: "auto" }}>
          <div className="d-flex flex-wrap gap-3">
            {displayColumns.map((col) => (
              <label key={col} className="small d-flex align-items-center gap-1">
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col)}
                  onChange={() => toggleColumn(col)}
                />
                <span>{col}</span>
              </label>
            ))}
          </div>
        </div>
        )}

        {!isPivotMode && (
        <div className="mb-2 border rounded">
          <button
            type="button"
            className="btn btn-link text-decoration-none w-100 text-start"
            style={{ color: "#0f2d5c", fontWeight: 700 }}
            onClick={() => setSummaryOpen((v) => !v)}
          >
            SUMMARY
          </button>
          {summaryOpen && (
            <div className="px-2 pb-2">
              <div className="d-flex flex-wrap gap-2 mb-2">
                {availableSummaryOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`btn btn-sm ${summaryMode === opt.key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setSummaryMode(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="small" style={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}>
                {summaryRows.map((item) => `${item.label} : ${item.count}`).join("\n")}
                {summaryRows.length > 0 ? "\n" : ""}
                {`Total : ${Array.isArray(sortedRows) ? sortedRows.length : 0}`}
              </div>
            </div>
          )}
        </div>
        )}

        {!isPivotMode && !isDaftarHadirTableMode && !isInputOdoFitMode && (
          <div
            ref={topScrollRef}
            onScroll={syncFromTop}
            className="border rounded mb-2 report-scroll-hover-x"
            style={{ overflowX: "auto", overflowY: "hidden", height: 16 }}
          >
            <div style={{ width: Math.max(scrollWidth, 1), height: 1 }}></div>
          </div>
        )}

        <div
          ref={bottomScrollRef}
          onScroll={syncFromBottom}
          className="border rounded report-scroll-hover-x"
          style={{ overflowX: (isPivotMode || isDaftarHadirTableMode || isInputOdoFitMode) ? "hidden" : "auto", overflowY: "auto", maxHeight: "60vh" }}
        >
          <div ref={tableRef}>
          {isPivotMode ? (
          <table className="table table-sm mb-0 report34-table" style={{ width: "100%", tableLayout: "fixed" }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "#0f2d5c", color: "#ffffff" }}>
              <tr>
                <th style={{ width: 30, whiteSpace: "nowrap", fontSize: "12px", padding: "0.2rem 0.1rem", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>NO.</th>
                <th style={{ width: 48, whiteSpace: "nowrap", fontSize: "12px", padding: "0.2rem 0.1rem", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>CABANG</th>
                <th style={{ width: 64, whiteSpace: "nowrap", fontSize: "12px", padding: "0.2rem 0.15rem", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>NAMA PEGAWAI</th>
                {daftarHadirPivotMeta.dayColumns.map((day) => (
                  <th
                    key={`pivot-day-${day}`}
                    className="text-center"
                    style={{ width: 22, whiteSpace: "nowrap", fontSize: "12px", padding: "0.2rem 0.05rem", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}
                  >
                    {day}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={Math.max(3, daftarHadirPivotMeta.dayColumns.length + 3)}
                    className="text-muted text-center"
                    style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
                  >
                    Tidak ada data.
                  </td>
                </tr>
              )}
              {pagedRows.map((row, idx) => (
                <tr key={`pivot-r-${start + idx}`}>
                  <td style={{ padding: "0.15rem 0.1rem", whiteSpace: "nowrap", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis" }}>{start + idx + 1}</td>
                  <td title={String(row?.CABANG || "-")} style={{ padding: "0.15rem 0.1rem", whiteSpace: "nowrap", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis" }}>{String(row?.CABANG || "-")}</td>
                  <td title={String(row?.["NAMA PEGAWAI"] || "-")} style={{ padding: "0.15rem 0.15rem", whiteSpace: "nowrap", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis" }}>{String(row?.["NAMA PEGAWAI"] || "-")}</td>
                  {daftarHadirPivotMeta.dayColumns.map((day) => {
                    const dateKey = `${daftarHadirPivotMeta.monthKey}-${String(day).padStart(2, "0")}`;
                    const status = String(row?.[`__status_${day}`] || "").trim();
                    const isFutureDate = Boolean(daftarHadirPivotMeta.todayKey && dateKey > daftarHadirPivotMeta.todayKey);
                    const style = buildAbsensiStatusStyle(status, isFutureDate);
                    const value = String(row?.[day] ?? "-").trim() || "-";
                    return (
                      <td
                        key={`pivot-cell-${start + idx}-${day}`}
                        className="text-center"
                        style={{
                          width: 22,
                          padding: "0.15rem 0.05rem",
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                          background: style.background,
                          color: style.color,
                          fontWeight: 600,
                        }}
                        title={status || (isFutureDate ? "Future Date" : "BELUM ABSEN")}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          ) : (
          <table
            className="table table-sm mb-0 report34-table"
            style={(isDaftarHadirTableMode || isInputOdoFitMode) ? { width: "100%", tableLayout: "fixed" } : undefined}
          >
            <thead style={{ position: "sticky", top: 0, zIndex: 2, background: "#0f2d5c", color: "#ffffff" }}>
              <tr>
                <th style={isInputOdoFitMode
                  ? { ...inputOdoBaseCellStyle, width: inputOdoNoWidth, whiteSpace: "nowrap", background: "#0f2d5c", color: "#ffffff", fontWeight: 400, textAlign: "center" }
                  : (isDaftarHadirTableMode ? { width: 34, whiteSpace: "nowrap", fontSize: "12px", padding: "0.2rem 0.1rem", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 } : { minWidth: 40, background: "#0f2d5c", color: "#ffffff", fontWeight: 400 })}>
                  {(isBanVsFormasi || isProfilVsEvent || isBanProfilVsLastEvent) ? "NO." : formatHeaderLabel("No")}
                </th>
                {selectedColumns.map((col) => (
                  (() => {
                    const isOdoDayColHeader = isInputOdoMt && isInputOdoDayColumn(col);
                    const isEventMtNumericHeader = isLaporanEventMt && isEventMtRightNumberColumn(col);
                    const isEventBanTdHeader = isLaporanEventBanLike && isEventBanTdColumn(col);
                    const isBanVsCenterHeader = isBanVsFormasi && isBanVsFormasiCenterColumn(col);
                    const isLampiranHeader = isLampiranColumn(col);
                    return (
                  <th
                    key={col}
                    className={(isBanVsCenterHeader || isLampiranHeader || isEventBanTdHeader) ? "text-center" : ((isOdoDayColHeader || isEventMtNumericHeader) ? "text-end" : "")}
	                    style={isInputOdoFitMode
                      ? {
                          ...inputOdoBaseCellStyle,
                          width: getInputOdoColumnWidth(col),
                          cursor: "grab",
                          whiteSpace: isOdoDayColHeader ? "nowrap" : "normal",
                          overflowWrap: isOdoDayColHeader ? undefined : "anywhere",
                          background: "#0f2d5c",
                          color: "#ffffff",
                          fontWeight: 400,
                          textAlign: isOdoDayColHeader ? "center" : "left",
                        }
                      : (isDaftarHadirTableMode
	                      ? {
	                          width: getDaftarHadirTableWidth(col),
                          cursor: "grab",
                          whiteSpace: "nowrap",
                          fontSize: "12px",
                          padding: "0.2rem 0.15rem",
                          background: "#0f2d5c",
                          color: "#ffffff",
	                          fontWeight: 400,
	                        }
	                      : { minWidth: 150, cursor: "grab", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 })}
                    draggable
                    onDragStart={() => setDraggingCol(col)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      moveColumn(draggingCol, col);
                      setDraggingCol("");
                    }}
                    onDragEnd={() => setDraggingCol("")}
                  >
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 text-decoration-none"
                      style={{
                        color: "#ffffff",
                        fontWeight: 400,
                        width: "100%",
	                        fontSize: isInputOdoFitMode ? "9px" : (isDaftarHadirTableMode ? "12px" : undefined),
	                        lineHeight: isInputOdoFitMode ? 1.1 : undefined,
	                        whiteSpace: isInputOdoFitMode ? (isOdoDayColHeader ? "nowrap" : "normal") : undefined,
	                        overflowWrap: isInputOdoFitMode && !isOdoDayColHeader ? "anywhere" : undefined,
	                        textAlign: isInputOdoFitMode ? (isOdoDayColHeader ? "center" : "left") : ((isBanVsCenterHeader || isLampiranHeader || isEventBanTdHeader) ? "center" : ((isOdoDayColHeader || isEventMtNumericHeader) ? "right" : "left")),
                      }}
                      onMouseEnter={() => setHoveredCol(col)}
                      onMouseLeave={() => setHoveredCol("")}
                      onClick={() => handleSort(col)}
                    >
                      {getHeaderLabel(col)}
                      {(hoveredCol === col || sortBy === col) && (
                        <span>{` ${sortBy === col ? (sortDir === "asc" ? "▲" : "▼") : "↕"}`}</span>
                      )}
                    </button>
                  </th>
                    );
                  })()
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={Math.max(1, selectedColumns.length + 1)}
                    className="text-muted text-center"
                    style={{ paddingTop: "0.25rem", paddingBottom: "0.25rem" }}
                  >
                    Tidak ada data.
                  </td>
                </tr>
              )}
	              {pagedRows.map((row, idx) => (
	                <tr key={`r-${start + idx}`}>
	                  <td style={isInputOdoFitMode
                      ? { ...inputOdoBaseCellStyle, width: inputOdoNoWidth, whiteSpace: "nowrap", textAlign: "center" }
                      : (isDaftarHadirTableMode ? { padding: "0.15rem 0.1rem", whiteSpace: "nowrap", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis" } : { paddingTop: "0.25rem", paddingBottom: "0.25rem" })}>{start + idx + 1}</td>
                  {selectedColumns.map((col) => {
                    const isNumberCol = isThousandDotColumn(col);
                    const isOdoDayCol = isInputOdoMt && isInputOdoDayColumn(col);
                    const isUpdateOdoValueCol = isLaporanUpdateOdo && normalizeColumnName(col) === "odo";
                    const isUpdateOdoTransactionCol = isLaporanUpdateOdo && normalizeColumnName(col) === "transaksi";
                    const isStokRightCol = isStokGudang && isStokGudangRightColumn(col);
                    const isEventMtNumericCol = isLaporanEventMt && isEventMtRightNumberColumn(col);
                    const isEventBanKmtSaatEventCol = isLaporanEventBanLike && isEventBanKmtSaatEventColumn(col);
                    const isEventBanTdCol = isLaporanEventBanLike && isEventBanTdColumn(col);
                    const isTdCol = isTdLaporanColumn(col);
                    const isKmtCol = isKmtLaporanColumn(col);
                    const isJarakPadCol = isJarakTempuhPadColumn(col);
                    const isWearRateCol = isWearRate && normalizeColumnName(col) === "wear rate";
                    const isWearRatePerMtPositionDateCol = isWearRatePerMtPosition && isWearRatePerMtPositionDateColumn(col);
                    const isWearRateSejakAwalNumericCol = isWearRateSejakAwal && isWearRateSejakAwalNumericColumn(col);
                    const isWearRateLastPositionNumericCol = isWearRateLastPosition && isWearRateLastPositionNumericColumn(col);
                    const isWearRateTdCorrectionWarning = ((isWearRateLastPosition && normalizeColumnName(col) === "wear rate di posisi")
                      || (isWearRateSejakAwal && normalizeColumnName(col) === "wear rate")
                      || (isWearRatePerMtPosition && normalizeColumnName(col) === "wear rate posisi"))
                      && hasWearRateTdCorrectionWarning(row);
                    const isWearRatePerMtPositionNumericCol = isWearRatePerMtPosition && isWearRatePerMtPositionNumericColumn(col);
                    const isBanVsCenterCol = isBanVsFormasi && isBanVsFormasiCenterColumn(col);
                    const isWearPadCol = isWearRateNumericPadColumn(col);
                    const isIdMtCol = isIdMtColumn(col);
                    const isIdBanCol = isIdBanColumn(col);
                    const isIdBanTerkaitCol = isIdBanTerkaitColumn(col);
                    const isNopolCol = normalizeColumnName(col) === "nopol";
                    const isSeriBanCol = normalizeColumnName(col) === "seri ban";
                    const isLampiranCol = isLampiranColumn(col);
                    const isFotoCol = isFotoColumn(col);
                    const isSupervisiCol = normalizeColumnName(col) === "supervisi";
                    const posisiState = getPosisiVisualState(row, col);
                    const odoState = getInputOdoVisualState(row, col, isInputOdoMt);
                    const updateOdoTransactionStyle = getInputOdoCellStyle(getUpdateOdoTransactionState(row?.[col]));
                    const supervisiRaw = isSupervisiCol ? row?.[col] : null;
                    const supervisiText = isSupervisiCol
                      ? (() => {
                          if (supervisiRaw == null) return "";
                          if (typeof supervisiRaw === "string") {
                            const trimmed = supervisiRaw.trim();
                            if (!trimmed || trimmed === "-" || trimmed.toLowerCase() === "null") return "";
                            return trimmed;
                          }
                          if (typeof supervisiRaw === "object") {
                            if (Array.isArray(supervisiRaw) && supervisiRaw.length === 0) return "";
                            try {
                              const text = JSON.stringify(supervisiRaw);
                              return text === "{}" || text === "[]" ? "" : text;
                            } catch {
                              return "";
                            }
                          }
                          return String(supervisiRaw).trim();
                        })()
                      : "";
                    const cellText = isWearRatePerMtPositionDateCol
                        ? formatWearRatePerMtPositionDate(row?.[col])
                        : (isOdoDayCol
                        ? (String(row?.[col] ?? "").trim() || "-")
                        : (isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol
                          ? formatThousandsDot(row?.[col])
                          : (isWearRateCol
                          ? formatDecimalId(row?.[col], 2)
	                      : (isSupervisiCol
	                        ? (supervisiText ? "DONE" : "-")
                        : ((isNumberCol || isOdoDayCol || isUpdateOdoValueCol || isStokRightCol || isWearRateLastPositionNumericCol || isWearRatePerMtPositionNumericCol)
	                          ? formatThousandsDot(row?.[col])
	                          : ((isEventMtNumericCol || isEventBanKmtSaatEventCol)
	                        ? formatThousandsDot(row?.[col])
                        : normalizePerubahanValue(col, row?.[col])))))));
                    const isTdDashCenter = isTdColumn(col) && isTdDashText(cellText);
                    const rawIdMt = String(row?.[col] ?? "").trim();
                    const isIdMtClickable = isIdMtCol && rawIdMt && rawIdMt !== "-";
                    const rawIdBan = String(row?.[col] ?? "").trim();
                    const isIdBanClickable = isIdBanCol && /^\d+$/.test(rawIdBan);
                    const rawNopol = String(row?.[col] ?? "").trim();
                    const isNopolClickable = isNopolCol && rawNopol && rawNopol !== "-";
                    const rawSeriBan = String(row?.[col] ?? "").trim();
                    const isSeriBanClickable = isSeriBanCol && rawSeriBan && rawSeriBan !== "-"
                      && resolveIdBanFromRow(row) > 0;
                    const idBanTokens = isIdBanTerkaitCol
                      ? String(row?.[col] ?? "")
                        .split("|")
                        .map((x) => String(x || "").trim())
                        .filter((x) => /^\d+$/.test(x))
                      : [];
                    const lampiranItemsRaw = isLampiranCol
                      ? (() => {
                          const src = row?.[col];
                          if (Array.isArray(src)) return src;
                          if (typeof src === "string") {
                            try {
                              const parsed = JSON.parse(src);
                              return Array.isArray(parsed) ? parsed : [];
                            } catch {
                              return [];
                            }
                          }
                          return [];
                        })()
                      : [];
                    const lampiranFilesMapped = isLampiranCol
                      ? lampiranItemsRaw
                        .map((item, i) => {
                          const rawPath = String(item?.path || item?.url || "").trim();
                          const rawFile = String(item?.file || "").trim();
                          const url = rawPath
                            ? (rawPath.startsWith("http") ? rawPath : `${backendPort}${rawPath}`)
                            : (rawFile ? `${backendPort}/uploads/dokumen/${rawFile}` : "");
                          if (!url) return null;
                          return {
                            url,
                            name: String(item?.name || rawFile || `Lampiran ${i + 1}`).trim(),
                            caption: String(item?.caption || item?.name || rawFile || `Lampiran ${i + 1}`).trim(),
                          };
                        })
                        .filter(Boolean)
                      : [];
                    const fotoRaw = isFotoCol ? String(row?.[col] ?? "").trim() : "";
                    const fotoUrl = isFotoCol && fotoRaw && fotoRaw !== "-"
                      ? (fotoRaw.startsWith("http://") || fotoRaw.startsWith("https://")
                        ? fotoRaw
                        : `${backendPort}${fotoRaw.startsWith("/") ? fotoRaw : `/${fotoRaw}`}`)
                      : "";
                    const odoStyle = getInputOdoCellStyle(odoState);
                    const cellBg = isWearRateTdCorrectionWarning
                      ? "#fecaca"
                      : (isUpdateOdoTransactionCol
                      ? updateOdoTransactionStyle.background
                      : (isOdoDayCol
                      ? odoStyle.background
                      : (posisiState === "inactive"
                        ? "#e5e7eb"
                        : (posisiState === "missing" || odoState === "missing")
                          ? "#fbcfe8"
                          : "transparent")));
                    const cellColor = isWearRateTdCorrectionWarning
                      ? "#991b1b"
                      : (isUpdateOdoTransactionCol
                      ? updateOdoTransactionStyle.color
                      : (isOdoDayCol ? odoStyle.color : undefined));
                    return (
                    <td
                      key={`${start + idx}-${col}`}
                      className={(isLampiranCol || isSupervisiCol) ? "text-center" : (isBanVsCenterCol ? "text-center" : ((isEventBanTdCol || isTdCol || isTdDashCenter) ? "text-center" : ((isNumberCol || isOdoDayCol || isUpdateOdoValueCol || isStokRightCol || isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol || isWearRatePerMtPositionNumericCol || isEventMtNumericCol || isEventBanKmtSaatEventCol) ? "text-end" : "")))}
	                      style={isInputOdoFitMode
                          ? {
                              ...inputOdoBaseCellStyle,
                              width: getInputOdoColumnWidth(col),
                              whiteSpace: isOdoDayCol ? "nowrap" : "normal",
                              overflowWrap: isOdoDayCol ? undefined : "anywhere",
                              textAlign: isOdoDayCol ? "center" : ((isLampiranCol || isSupervisiCol || isBanVsCenterCol || isEventBanTdCol || isTdCol || isTdDashCenter) ? "center" : "left"),
                              background: cellBg,
                              color: cellColor,
                            }
                          : {
                              paddingTop: isDaftarHadirTableMode ? "0.15rem" : "0.25rem",
                              paddingBottom: isDaftarHadirTableMode ? "0.15rem" : "0.25rem",
                              paddingLeft: isDaftarHadirTableMode ? "0.15rem" : (isKmtCol ? "20px" : undefined),
                              fontSize: isDaftarHadirTableMode ? "12px" : undefined,
                              whiteSpace: isDaftarHadirTableMode ? "nowrap" : undefined,
                              overflow: isDaftarHadirTableMode ? "hidden" : undefined,
                              textOverflow: isDaftarHadirTableMode ? "ellipsis" : undefined,
                              paddingRight: isStokRightCol ? "30px" : (isKmtCol ? "20px" : (isJarakPadCol ? "20px" : (isWearPadCol ? "20px" : undefined))),
                              background: cellBg,
                              color: cellColor,
                            }}
                      title={isWearRateTdCorrectionWarning
                        ? WR_TD_CORRECTION_WARNING_TOOLTIP
                        : ((isDaftarHadirTableMode || isInputOdoFitMode) ? String(cellText || row?.[col] || "-") : undefined)}
                    >
                      <div
                        style={{
	                          maxWidth: (isDaftarHadirTableMode || isInputOdoFitMode) ? "100%" : CELL_MAX_WIDTH,
	                          overflowX: (isDaftarHadirTableMode || isInputOdoFitMode) ? "hidden" : "auto",
	                          overflowY: "hidden",
	                          whiteSpace: isInputOdoFitMode ? (isOdoDayCol ? "nowrap" : "normal") : "nowrap",
	                          overflowWrap: isInputOdoFitMode && !isOdoDayCol ? "anywhere" : undefined,
	                          textOverflow: isDaftarHadirTableMode ? "ellipsis" : undefined,
	                          textAlign: isBanVsCenterCol
	                            ? "center"
	                            : ((isLampiranCol || isSupervisiCol)
	                              ? "center"
	                              : (isInputOdoFitMode && isOdoDayCol
                                  ? "center"
                      : ((isEventBanTdCol || isTdCol || isTdDashCenter) ? "center" : ((isNumberCol || isOdoDayCol || isUpdateOdoValueCol || isStokRightCol || isWearRateCol || isWearRateSejakAwalNumericCol || isWearRateLastPositionNumericCol || isWearRatePerMtPositionNumericCol || isEventMtNumericCol || isEventBanKmtSaatEventCol) ? "right" : "left")))),
                        }}
                      >
                        {isIdMtClickable ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            style={{ color: "#0d6efd", textDecoration: "underline" }}
                            onClick={() => {
                              setHistoryMtKeyword(rawIdMt);
                              setOpenHistoryMtModal(true);
                            }}
                          >
                            {rawIdMt}
                          </button>
                        ) : isNopolClickable ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            style={{ color: "#0d6efd", textDecoration: "underline" }}
                            onClick={() => {
                              if (isMonitoringBan) {
                                openMtDetailFromNopol(row);
                                return;
                              }
                              openHistoryMtFromNopol(row);
                            }}
                          >
                            {rawNopol}
                          </button>
                        ) : isIdBanClickable ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            style={{ color: "#0d6efd", textDecoration: "underline" }}
                            onClick={() => {
                              setHistoryBanKeyword(rawIdBan);
                              setOpenHistoryBanModal(true);
                            }}
                          >
                            {rawIdBan}
                          </button>
                        ) : isSeriBanClickable ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0"
                            style={{ color: "#0d6efd", textDecoration: "underline" }}
                            onClick={() => {
                              if (isMonitoringBan) {
                                openBanDetailFromSeri(row);
                                return;
                              }
                              const idBan = resolveIdBanFromRow(row);
                              if (!Number.isFinite(idBan) || idBan <= 0) return;
                              setHistoryBanKeyword(String(idBan));
                              setOpenHistoryBanModal(true);
                            }}
                          >
                            {rawSeriBan}
                          </button>
                        ) : isIdBanTerkaitCol && idBanTokens.length > 0 ? (
                          <div className="d-flex flex-wrap gap-1">
                            {idBanTokens.map((idBan) => (
                              <button
                                key={`${start + idx}-${col}-${idBan}`}
                                type="button"
                                className="btn btn-link btn-sm p-0"
                                style={{ color: "#0d6efd", textDecoration: "underline" }}
                                onClick={() => {
                                  setHistoryBanKeyword(idBan);
                                  setOpenHistoryBanModal(true);
                                }}
                              >
                                {idBan}
                              </button>
                            ))}
                          </div>
                        ) : isLampiranCol ? (
                          lampiranFilesMapped.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                setLampiranTitle("Lampiran Supervisi");
                                setLampiranFiles(lampiranFilesMapped);
                                setShowLampiranModal(true);
                              }}
                            >
                              <i className="fa fa-paperclip me-1" aria-hidden="true" />
                              {`Lampiran (${lampiranFilesMapped.length})`}
                            </button>
                          ) : (
                            "-"
                          )
                        ) : isFotoCol ? (
                          fotoUrl ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm"
                              onClick={() => {
                                setLampiranTitle("Foto Absensi");
                                setLampiranFiles([{
                                  url: fotoUrl,
                                  name: String(row?.Nama || row?.nama || "Foto absensi").trim(),
                                  caption: String(row?.Nama || row?.nama || "Foto absensi").trim(),
                                }]);
                                setShowLampiranModal(true);
                              }}
                            >
                              Lihat Foto
                            </button>
                          ) : (
                            "-"
                          )
                        ) : (
                          cellText
                        )}
                      </div>
                    </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          )}
          </div>
        </div>

        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={safePage <= 1 || pageSize === "All"}
          >
            Prev
          </button>
          <span className="small text-muted">{`Page ${safePage} / ${totalPages}`}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={safePage >= totalPages || pageSize === "All"}
          >
            Next
          </button>
        </div>
      </section>
      {showRekapAbsensiModal && (
        <>
          <div className="modal-backdrop fade show" style={{ zIndex: 1090 }} onClick={() => setShowRekapAbsensiModal(false)} />
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true" style={{ zIndex: 1095 }}>
            <div className="modal-dialog modal-dialog-scrollable" role="document" style={{ width: "96vw", maxWidth: "96vw" }}>
              <div className="modal-content">
                <div className="modal-header align-items-start">
                  <div>
                    <h5 className="modal-title mb-1">{`REKAPITULASI ABSENSI PEGAWAI - ${rekapAbsensiPeriodLabel}`}</h5>
                    <div className="small text-muted">{`${rekapAbsensiDisplayRows.length} pegawai aktif`}</div>
                  </div>
                  <div className="ms-auto d-flex justify-content-end gap-2 align-items-center">
                    <button type="button" className="btn btn-success btn-sm" onClick={downloadRekapAbsensi}>DOWNLOAD</button>
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowRekapAbsensiModal(false)}>CLOSE</button>
                  </div>
                </div>
                <div className="modal-body">
                  {rekapAbsensiError ? <div className="alert alert-danger py-2">{rekapAbsensiError}</div> : null}
                  <div className="table-responsive" style={{ maxHeight: "65vh" }}>
                    <table className="table table-sm table-bordered mb-0 report34-table">
                      <thead style={{ position: "sticky", top: 0, zIndex: 1, background: "#0f2d5c", color: "#ffffff" }}>
                        <tr>
                          {rekapAbsensiHeaders.map((header) => (
                            <th key={`rekap-absensi-${header.key}`} className={["no", "hadir", "libur", "ijin", "sakit", "cuti", "dinas_luar", "alpa", "non_aktif", "lainnya", "belum_absen", "total_hari"].includes(header.key) ? "text-end" : ""} style={{ whiteSpace: "nowrap", background: "#0f2d5c", color: "#ffffff", fontWeight: 400 }}>
                              {header.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rekapAbsensiDisplayRows.length === 0 ? (
                          <tr><td colSpan={rekapAbsensiHeaders.length} className="text-center text-muted">Tidak ada pegawai aktif.</td></tr>
                        ) : rekapAbsensiDisplayRows.map((row) => (
                          <tr key={`rekap-absensi-row-${row.no}`}>
                            {rekapAbsensiHeaders.map((header) => (
                              <td key={`rekap-absensi-${row.no}-${header.key}`} className={["no", "hadir", "libur", "ijin", "sakit", "cuti", "dinas_luar", "alpa", "non_aktif", "lainnya", "belum_absen", "total_hari"].includes(header.key) ? "text-end" : ""}>
                                {row?.[header.key] ?? "-"}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {showMtDetailModal && selectedMtDetail && (
        <div className="page16-modal-backdrop" role="dialog" aria-modal="true">
          <div
            className="page16-modal-card"
            style={{ maxWidth: "95vw", width: "95vw", maxHeight: "95vh", overflowY: "auto" }}
          >
            <PageKonten9_Show
              globalOpsi={globalOpsi}
              editData={selectedMtDetail}
              setShowDetail={setShowMtDetailModal}
              setShowMaster={() => {}}
              reloadToken={0}
            />
          </div>
        </div>
      )}
      <ModalBanShow
        open={showBanDetailModal}
        onClose={() => setShowBanDetailModal(false)}
        globalOpsi={globalOpsi}
        idBan={selectedBanId}
        selectedPosisi={selectedBanPosisi}
      />
      <Page35_TabelHistoryMT
        open={openHistoryMtModal}
        onClose={() => setOpenHistoryMtModal(false)}
        initialKeyword={historyMtKeyword}
        opsiCabang={opsiCabang}
        globalOpsi={globalOpsi}
      />
      <Page36_TabelHistoryBan
        open={openHistoryBanModal}
        onClose={() => setOpenHistoryBanModal(false)}
        initialKeyword={historyBanKeyword}
        opsiCabang={opsiCabang}
        globalOpsi={globalOpsi}
      />
      <ShowPic
        open={showLampiranModal}
        onClose={() => {
          setShowLampiranModal(false);
          setLampiranFiles([]);
          setLampiranTitle("Lampiran Laporan");
        }}
        row={{}}
        backendPort={backendPort}
        token=""
        readOnly={true}
        customFiles={lampiranFiles}
        customTitle={lampiranTitle}
        zIndex={1085}
      />
    </>
  );
}
